import { useState, useEffect, useContext, useRef, useCallback, useMemo } from "react";
import { AuthContext } from "../../../../../context/AuthContext";
import Table from "../../../../../components/ui/Table";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Modal from "../../../../../components/ui/Modal";
import SmartSplitDocumentViewer from "./SmartSplitDocumentViewer";
import BulkAnnotationModal from "./BulkAnnotationModal";
import ScoreAdjustmentModal from "./ScoreAdjustmentModal";
import { Card } from "../../../../../components/ui/Card";
import "../../../../../App.css";
import Loader from "../../../../../components/ui/Loader";
import ModalTableInput from "../../../../../components/ui/ModalTableInput";
import {
  swalConfirm,
  swalError,
  swalSuccess,
  swalWarning,
} from "../../../../../utils/swalAlert";
import {
  showScoreMismatchAlert,
  promptEditMaxScore,
} from "../../../../../utils/userPromptUtils";
import { fetchWithAuth } from "../../../../../utils/api";
import PerformanceReport from "../../../../ui/PerformanceReport";
import { useReactToPrint } from "react-to-print";
import SmartDownloadButton from "../../../../../components/ui/SmartDownloadButton";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import certificateService from "../../../../../services/certificateService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faDownload } from "@fortawesome/free-solid-svg-icons";
import AutofillMarksModal, { generateAndDownloadTemplate } from "./AutofillMarksModal";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const SubmittedAssessment = ({
  testId,
  classInfo,
  testInfo,
  isManualEntryTest = false,
  isOnlineExamination = false,
  isHomeworkTest = false,
  initialInstituteInfo = null,
  autoOpenSpecialInstructions = false,
  scoreMismatchDetails = null,
}) => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const hasHandledScoreMismatchRef = useRef(false);
  const [data, setData] = useState([]);
  const [pendingSubmissionCount, setPendingSubmissionCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [serverGradedCount, setServerGradedCount] = useState(0);
  const [overallTotalRecords, setOverallTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isSpecialModalOpen, setIsSpecialModalOpen] = useState(false);
  const [specialInstruction, setSpecialInstruction] = useState("");
  const [userPrompt, setUserPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPrevPrompt, setAiPrevPrompt] = useState(null);
  const [aiHasActions, setAiHasActions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [testDetails, setTestDetails] = useState({});

  const isHomework = Boolean(
    isHomeworkTest ||
    location.state?.isHomeworkTest ||
    location.state?.isHomework ||
    testDetails?.isHomeworkTest === true ||
    testDetails?.isHomeworkTest === "true" ||
    testDetails?.isHomework === true ||
    testDetails?.isHomework === "true" ||
    testInfo?.isHomeworkTest === true ||
    testInfo?.isHomeworkTest === "true" ||
    testInfo?.isHomework === true ||
    testInfo?.isHomework === "true"
  );
  const [isSummaryButtonActive, setIsSummaryButtonActive] = useState(false);
  const [summarizePendingCount, setSummarizePendingCount] = useState(0);
  const [isSummaryGenerating] = useState(false);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [currentAnswerPaper, setCurrentAnswerPaper] = useState(null);
  const [instituteInfo, setInstituteInfo] = useState(initialInstituteInfo || null);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishConfirmInput, setPublishConfirmInput] = useState("");
  const [publishStep, setPublishStep] = useState(1); // 1 = choose method, 2 = warning and confirm
  const [publishMethod, setPublishMethod] = useState(null); // 'just-publish', 'notify-without-pdf', 'notify-with-pdf'
  const [activeAction, setActiveAction] = useState({ id: null, type: null });
  const [publishPhase, setPublishPhase] = useState("");
  const [publishProgress, setPublishProgress] = useState("");
  const [currentStudentName, setCurrentStudentName] = useState("");
  const [publishWithAnswerPaper, setPublishWithAnswerPaper] = useState(false);
  const [classId, setClassId] = useState(classInfo?.id || null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    try {
      const saved = localStorage.getItem("table_page_size");
      if (saved) {
        const parsed = parseInt(saved, 10);
        if ([10, 25, 50, 75, 100].includes(parsed)) {
          return parsed;
        }
      }
    } catch (err) {
      console.error("Error reading table_page_size in SubmittedAssessments:", err);
    }
    return 25;
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const [isMarksModalOpen, setIsMarksModalOpen] = useState(false);
  const [isAutofillModalOpen, setIsAutofillModalOpen] = useState(false);
  const [editingScoreId, setEditingScoreId] = useState(null);
  const [allStudentsMarks, setAllStudentsMarks] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState({
    isOpen: false,
    studentIndex: null,
    taskIndex: null,
    feedback: "",
  });
  const [modifiedStudents, setModifiedStudents] = useState(new Set());
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportType, setExportType] = useState(null); // 'summarized' or 'detailed'
  const [exportProgress, setExportProgress] = useState({
    step: 0,
    message: "",
  });
  const [isExportInProgress, setIsExportInProgress] = useState(false);
  const [isNotifyAllModalOpen, setIsNotifyAllModalOpen] = useState(false);
  const [isRegradeModalOpen, setIsRegradeModalOpen] = useState(false);
  const [regradeProgress, setRegradeProgress] = useState({
    current: 0,
    total: 0,
    message: "",
  });
  const [isGeneratingCerts, setIsGeneratingCerts] = useState(false);
  const [isBulkAnnotationModalOpen, setIsBulkAnnotationModalOpen] = useState(false);

  // Row selection state for Score Modifier / Table
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());

  // Score Modifier modal state
  const [scoreAdjustModal, setScoreAdjustModal] = useState({ isOpen: false, student: null });
  const setIsScoreModifierModalOpen = (val) => setScoreAdjustModal({ isOpen: val, student: null });

  const [gradingPolicy, setGradingPolicy] = useState("Lenient");
  const [thresholdPercentage, setThresholdPercentage] = useState(0.25);
  const [scoreModifierResults, setScoreModifierResults] = useState(null);
  const [isCheckingScore, setIsCheckingScore] = useState(false);
  const [selectedModifierRowIds, setSelectedModifierRowIds] = useState(new Set());
  const [isApplyingPolicy, setIsApplyingPolicy] = useState(false);


  // Smart Split Document Viewer States
  const [isSmartSplitModalOpen, setIsSmartSplitModalOpen] = useState(false);
  const [smartSplitResult, setSmartSplitResult] = useState(null); // { pages, papers }
  const [smartSplitLoading, setSmartSplitLoading] = useState(false);
  const [smartSplitStatus, setSmartSplitStatus] = useState(null);

  const summaryRef = useRef(null);

  const navigate = useNavigate();

  const fetchSmartSplitStatus = useCallback(async () => {
    try {
      const res = await fetchWithAuth(
        `${serverUrl}/api/smart-split/${testId}/status`,
        { method: "GET", credentials: "include" }
      );
      if (!res || !res.ok) return;
      const data = await res.json();
      setSmartSplitStatus({
        status: data.status || 'idle',
        processedCount: data.processedCount || 0,
        totalPages: data.totalPages || 0,
        papersCount: data.papers ? data.papers.length : 0,
        processingMode: data.processingMode || 'online',
        errorMessage: data.errorMessage || null,
        data,
      });
      if ((data.status === 'completed' || data.status === 'done') && data.papers?.length > 0) {
        setSmartSplitResult({
          pages: data.pages,
          papers: data.papers,
          masterPdfUrl: data.masterPdfUrl,
          scanOrderOffset: data.scanOrderOffset || 0,
          enrollments: data.enrollments || [],
        });
      }
    } catch (err) {
      console.warn("[SmartSplit] Could not fetch status:", err);
    }
  }, [testId]);

  useEffect(() => {
    fetchSmartSplitStatus();
  }, [fetchSmartSplitStatus]);

  // Polling if queued or processing
  useEffect(() => {
    let timer = null;
    if (smartSplitStatus?.status === 'processing' || smartSplitStatus?.status === 'queued') {
      timer = setInterval(() => {
        fetchSmartSplitStatus();
      }, 7000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [smartSplitStatus?.status, fetchSmartSplitStatus]);

  // Hide Smart Split button when all papers are submitted (0 in review & 0 in ready)
  const shouldShowSmartSplit = useMemo(() => {
    if (!smartSplitStatus) return false;
    if (smartSplitStatus.status === 'queued' || smartSplitStatus.status === 'processing') {
      return true;
    }
    if (smartSplitStatus.status === 'failed') {
      return true;
    }
    if (smartSplitStatus.status === 'completed' || smartSplitStatus.status === 'done') {
      const papers = smartSplitStatus.data?.papers || [];
      if (!papers.length) return false;
      const pendingCount = papers.filter((p) => !p.isSubmitted && !p.answerPaperId).length;
      return pendingCount > 0;
    }
    return false;
  }, [smartSplitStatus]);

  const handleOpenSmartSplitViewer = async () => {
    if (smartSplitStatus?.status === "processing" || smartSplitStatus?.status === "queued") {
      const isBackground = smartSplitStatus.processingMode === "batch";
      const progressText = smartSplitStatus.totalPages > 0
        ? `${smartSplitStatus.processedCount || 0} of ${smartSplitStatus.totalPages} pages completed`
        : "Preparing pages...";
      const etaText = isBackground
        ? "Background processing usually takes 15–20 minutes for large scans. You can safely close this page or work on other things."
        : "This usually takes just a minute or two. Please hold on.";

      swalWarning(
        `We're currently splitting and organizing your uploaded answer papers (${progressText}).\n\n${etaText}\n\nYour student answer booklets will be ready to review right here as soon as processing finishes.`,
        {
          title: "Preparing Answer Sheets",
          showConfirmButton: true,
          confirmButtonText: "Got It",
          timer: null,
        }
      );
      return;
    }
    if (smartSplitStatus?.status === "failed") {
      swalError(
        "We couldn't finish preparing the scan pages. Please return to the Pending Assessments page to upload your PDF again.",
        {
          title: "Could Not Complete",
          showConfirmButton: true,
          confirmButtonText: "Close",
          timer: null,
        }
      );
      return;
    }
    setSmartSplitLoading(true);
    try {
      const serverUrl = process.env.REACT_APP_SERVER_URL || "";
      const res = await fetchWithAuth(
        `${serverUrl}/api/smart-split/${testId}/status`,
        { method: "GET", credentials: "include" }
      );
      if (res && res.ok) {
        const statusData = await res.json();
        setSmartSplitStatus({
          status: statusData.status || 'idle',
          processedCount: statusData.processedCount || 0,
          totalPages: statusData.totalPages || 0,
          papersCount: statusData.papers ? statusData.papers.length : 0,
          processingMode: statusData.processingMode || 'online',
          errorMessage: statusData.errorMessage || null,
          data: statusData,
        });

        if (statusData.status === "processing" || statusData.status === "queued") {
          const isBackground = statusData.processingMode === "batch";
          const progressText = statusData.totalPages > 0
            ? `${statusData.processedCount || 0} of ${statusData.totalPages} pages completed`
            : "Preparing pages...";
          const etaText = isBackground
            ? "Background processing usually takes 15–20 minutes for large scans. You can safely close this page or work on other things."
            : "This usually takes just a minute or two. Please hold on.";

          swalWarning(
            `We're currently splitting and organizing your uploaded answer papers (${progressText}).\n\n${etaText}\n\nYour student answer booklets will be ready to review right here as soon as processing finishes.`,
            {
              title: "Preparing Answer Sheets",
              showConfirmButton: true,
              confirmButtonText: "Got It",
              timer: null,
            }
          );
          return;
        }

        if (statusData.status === "failed") {
          swalError(
            "We couldn't finish preparing the scan pages. Please return to the Pending Assessments page to upload your PDF again.",
            {
              title: "Could Not Complete",
              showConfirmButton: true,
              confirmButtonText: "Close",
              timer: null,
            }
          );
          return;
        }

        if (!statusData.papers || statusData.papers.length === 0) {
          swalWarning(
            "No split answer papers have been prepared for this test yet. To automatically divide and match student booklets from a single scan, go to the Pending Assessments page and upload your PDF via Smart Split.",
            {
              title: "No Split Papers Found",
              showConfirmButton: true,
              confirmButtonText: "Understood",
              timer: null,
            }
          );
          return;
        }

        setSmartSplitResult({
          testId,
          pages: statusData.pages,
          papers: statusData.papers,
          masterPdfUrl: statusData.masterPdfUrl,
          scanOrderOffset: statusData.scanOrderOffset || 0,
          enrollments: statusData.enrollments || [],
        });
        setIsSmartSplitModalOpen(true);
        return;
      }
    } catch (refreshErr) {
      console.warn("[SmartSplit] Could not fetch fresh status before opening, falling back to cached state:", refreshErr);
    } finally {
      setSmartSplitLoading(false);
    }

    // Fallback: If offline or status request failed, use cached state if completed
    if ((smartSplitStatus?.status === "completed" || smartSplitStatus?.status === "done") && smartSplitStatus?.data?.papers?.length > 0) {
      setSmartSplitResult({
        testId,
        pages: smartSplitStatus.data.pages,
        papers: smartSplitStatus.data.papers,
        masterPdfUrl: smartSplitStatus.data.masterPdfUrl,
        scanOrderOffset: smartSplitStatus.data.scanOrderOffset || 0,
        enrollments: smartSplitStatus.data.enrollments || [],
      });
      setIsSmartSplitModalOpen(true);
    }
  };

  // Fetch institute info once so we can pass it when rendering PDFs (hidden root won't have context)
  useEffect(() => {
    const fetchInstitute = async () => {
      try {
        if (!user?.instituteId) return;
        const resp = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${user.instituteId}`,
          { credentials: "include" },
        );
        if (!resp) return;
        const json = await resp.json();
        if (resp.ok) {
          setInstituteInfo(json);
        }
      } catch (err) {
        swalError("Failed to fetch institute info");
      }
    };
    fetchInstitute();
  }, [user?.instituteId]);

  useEffect(() => {
    if (initialInstituteInfo) {
      setInstituteInfo(initialInstituteInfo);
    }
  }, [initialInstituteInfo]);

  useEffect(() => {
    if (classInfo?.id) {
      setClassId(classInfo.id);
    }
  }, [classInfo?.id]);

  useEffect(() => {
    if (isManualEntryTest) {
      const fetchTestData = async () => {
        try {
          const response = await fetchWithAuth(
            `${serverUrl}/api/test/get/${testId}`,
            {
              method: "GET",
              credentials: "include",
            },
          );
          if (!response) return;
          const testData = await response.json();
          if (!response.ok) {
            swalError(testData.message || "Failed to fetch test data");
            return;
          }
          setTasks(testData.manualEntryQuestions || []);
        } catch (error) {
          console.error("Failed to fetch test data:", error);
        }
      };
      fetchTestData();
    }
  }, [testId, isManualEntryTest]);

  // Helper function to copy score to clipboard
  const copyScore = (score) => {
    navigator.clipboard.writeText(String(score));
    swalSuccess("Score copied to clipboard.");
  };

  // Helper function to open Google Classroom external link
  const openGoogleClassroom = async (answerPaperId) => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/answer-paper/get/${answerPaperId}`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      if (!response) return;
      const responseData = await response.json();
      if (!response.ok) {
        swalError(responseData.message || "Failed to fetch data");
      }
      response.data = responseData;
      const externalLink = response.data.externalAlternateLink;
      if (externalLink && externalLink !== "") {
        window.open(externalLink, "_blank");
      } else {
        swalError("Google Classroom link not available.");
      }
    } catch (error) {
      swalError("Failed to retrive Google Classroom link!");
    }
  };

  // Helper to fetch and combine enrollment and answer paper data for manual tests
  const getManualAssessmentData = async (testId, classId) => {
    try {
      // 1. Fetch all enrollments for the class
      const enrollmentResp = await fetchWithAuth(
        `${serverUrl}/api/enrollment/get-all/${classId}`,
        { credentials: "include" },
      );
      if (!enrollmentResp || !enrollmentResp.ok) {
        const error = await enrollmentResp?.json();
        swalError(error?.message || "Failed to fetch student enrollments.");
        return [];
      }
      const enrollments = await enrollmentResp.json();

      // 2. Fetch all submitted answer papers for the test
      const answerPaperResp = await fetchWithAuth(
        `${serverUrl}/api/answer-paper/get-all/${testId}`,
        { credentials: "include" },
      );
      if (!answerPaperResp || !answerPaperResp.ok) {
        console.warn(
          "Could not fetch answer papers, showing enrollments only.",
        );
      }
      const answerPapers = answerPaperResp ? await answerPaperResp.json() : [];

      // 3. Create a map of answer papers keyed by studentId for efficient lookup
      const answerPaperMap = new Map();
      if (Array.isArray(answerPapers)) {
        answerPapers.forEach((ap) => {
          if (ap && ap.studentId) {
            const sId = (ap.studentId?._id || ap.studentId).toString();
            answerPaperMap.set(sId, ap);
          }
        });
      }

      // 4. Merge enrollment data with answer paper data
      const enrichedData = (enrollments || []).map((enrollment) => {
        const studentId = (
          enrollment.studentId?._id ||
          enrollment.studentId ||
          enrollment._id ||
          ""
        ).toString();
        const matchingAnswerPaper = answerPaperMap.get(studentId);

        if (matchingAnswerPaper) {
          // Student has a submission, merge data, prioritizing answer paper info
          return {
            ...enrollment,
            ...matchingAnswerPaper,
            _id: matchingAnswerPaper._id,
            studentId: studentId,
            studentName: `${
              matchingAnswerPaper.firstName || enrollment.firstName || ""
            } ${
              matchingAnswerPaper.lastName || enrollment.lastName || ""
            }`.trim(),
            rollNo: matchingAnswerPaper.rollNo || enrollment.rollNo || "",
            status: matchingAnswerPaper.status || "Pending",
            isEnrollment: false, // Has a submission
            _rowStyle: matchingAnswerPaper.proctoringLog?.violations?.length > 0 ? { backgroundColor: 'rgba(239, 68, 68, 0.25)' } : undefined,
          };
        } else {
          // Student is enrolled but has not submitted
          return {
            ...enrollment,
            _id: studentId,
            studentId: studentId,
            studentName: `${enrollment.firstName || ""} ${
              enrollment.lastName || ""
            }`.trim(),
            rollNo: enrollment.rollNo || "",
            status: "Pending",
            isEnrollment: true, // No submission, from enrollment list
          };
        }
      });

      return enrichedData;
    } catch (error) {
      swalError(error.message || "Failed to build student list.");
      return [];
    }
  };

  // Fetch Submitted Assessments Data
  const fetchData = useCallback(
    async (currentPage = 1, currentSearch = "", currentSortBy = sortBy, currentSortOrder = sortOrder) => {
      setLoading(true);
      try {
        let enrichedData = [];
        if (isManualEntryTest) {
          const targetClassId = classInfo?.id || classId;
          if (targetClassId) {
            enrichedData = await getManualAssessmentData(testId, targetClassId);
          }
          setData(enrichedData);
          setTotalRecords(enrichedData.length);
          setOverallTotalRecords(enrichedData.length);
          const manualPending = enrichedData.filter(
            (item) => item.status !== "Graded",
          ).length;
          const manualGraded = enrichedData.filter(
            (item) => item.status === "Graded",
          ).length;
          setPendingCount(manualPending);
          setServerGradedCount(manualGraded);
        } else {
          const queryParams = new URLSearchParams({
            page: currentPage,
            limit: pageSize,
          });
          if (currentSearch && currentSearch.trim() !== "") {
            queryParams.append("search", currentSearch.trim());
          }
          if (currentSortBy && currentSortBy.trim() !== "") {
            queryParams.append("sortBy", currentSortBy.trim());
            queryParams.append("sortOrder", currentSortOrder || "asc");
          }
          const response = await fetchWithAuth(
            `${serverUrl}/api/answer-paper/get-all/${testId}?${queryParams.toString()}`,
            {
              method: "GET",
              credentials: "include",
            },
          );
          if (!response) return;
          const resData = await response.json();
          if (!response.ok) {
            swalError(resData.message || "Failed to fetch data");
          }

          const answerPapersList = Array.isArray(resData)
            ? resData
            : resData.answerPapers || [];
          const total =
            typeof resData.total === "number"
              ? resData.total
              : answerPapersList.length;

          let pCount = resData.pendingCount || 0;
          let sPendingCount = resData.summaryPendingCount || 0;
          let gCount =
            typeof resData.gradedCount === "number"
              ? resData.gradedCount
              : answerPapersList.filter((item) => item.status === "Graded").length;
          let overallTotal = total;

          if (currentSearch && currentSearch.trim() !== "") {
            try {
              const overallResponse = await fetchWithAuth(
                `${serverUrl}/api/answer-paper/get-all/${testId}?page=1&limit=1`,
                {
                  method: "GET",
                  credentials: "include",
                },
              );
              if (overallResponse && overallResponse.ok) {
                const overallData = await overallResponse.json();
                pCount = overallData.pendingCount || 0;
                sPendingCount = overallData.summaryPendingCount || 0;
                gCount =
                  typeof overallData.gradedCount === "number"
                    ? overallData.gradedCount
                    : gCount;
                overallTotal =
                  typeof overallData.total === "number"
                    ? overallData.total
                    : overallTotal;
              }
            } catch (err) {
              console.error("Failed to fetch overall pending counts:", err);
            }
          }

          enrichedData = answerPapersList.map((item) => ({
            ...item,
            studentName: `${item.firstName || ""} ${
              item.lastName || ""
            }`.trim(),
            rollNo: item.rollNo || "",
            status: item.status || "Pending",
            _rowStyle:
              item.proctoringLog?.violations?.length > 0
                ? { backgroundColor: "rgba(239, 68, 68, 0.25)" }
                : undefined,
          }));
          setData(enrichedData);
          setTotalRecords(total);
          setOverallTotalRecords(overallTotal);
          setPendingCount(pCount);
          setServerGradedCount(gCount);
          setSummarizePendingCount(sPendingCount);
          setIsSummaryButtonActive(sPendingCount > 0);
        }
      } catch (error) {
        swalError(error.error || "Failed to fetch data.");
      } finally {
        setLoading(false);
      }
    },
    [isManualEntryTest, classId, classInfo?.id, testId, pageSize, sortBy, sortOrder],
  );

  const handleSortChange = (key, direction) => {
    const order = direction === "descending" || direction === "desc" ? "desc" : "asc";
    setSortBy(key);
    setSortOrder(order);
    setPage(1);
  };

  const effectiveClassId = classInfo?.id || classId;
  useEffect(() => {
    if (isManualEntryTest) {
      if (effectiveClassId) {
        fetchData(1, "", "", "");
      }
    } else {
      fetchData(page, searchTerm, sortBy, sortOrder);
    }
  }, [fetchData, testId, isManualEntryTest, effectiveClassId, page, pageSize, searchTerm, sortBy, sortOrder]);

  // Fetch pending submissions (enrollments without submissions)
  useEffect(() => {
    const fetchPendingSubmissions = async () => {
      if (isManualEntryTest) {
        setPendingSubmissionCount(0);
        return;
      }
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/answer-paper/get/pending-submissions/test/${testId}?countOnly=true`,
          {
            method: "GET",
            credentials: "include",
          },
        );
        if (!response) return;
        const data = await response.json();
        if (!response.ok) {
          swalError(data.message || "Failed to fetch pending submissions");
        }
        setPendingSubmissionCount(typeof data.count === "number" ? data.count : (Array.isArray(data) ? data.length : 0));
      } catch (error) {}
    };
    fetchPendingSubmissions();
  }, [testId, isManualEntryTest]);

  // Preload PDF.js library for document viewer and PDF rendering
  useEffect(() => {
    if (!window.pdfjsLib) {
      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
      script.async = true;
      script.onload = () => {
        if (window.pdfjsLib) {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
        }
      };
      document.head.appendChild(script);
    } else if (
      window.pdfjsLib.GlobalWorkerOptions &&
      !window.pdfjsLib.GlobalWorkerOptions.workerSrc
    ) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
    }
  }, []);

  // Fetch test details to get userPrompt and scorePublishedDate
  useEffect(() => {
    const fetchTestData = async () => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/test/get/${testId}?onlyInfo=true`,
          {
            method: "GET",
            credentials: "include",
          },
        );
        if (!response) return;
        const testData = await response.json();
        if (!response.ok) {
          swalError(testData.message || "Failed to fetch test data");
        }

        setUserPrompt(testData.userPrompt);
        setClassId(testData.classId);
        setTestDetails(testData);
      } catch (error) {}
    };
    fetchTestData();
  }, [testId]);

  // Autofill specialInstruction when the modal opens or when userPrompt changes
  useEffect(() => {
    if (isSpecialModalOpen) {
      setSpecialInstruction(userPrompt);
    }
  }, [isSpecialModalOpen, userPrompt]);

  const handleUpdateMaxScore = useCallback(
    async (newMaxScore) => {
      try {
        setLoading(true);
        const response = await fetchWithAuth(
          `${serverUrl}/api/test/update/${testId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({ maxScore: newMaxScore }),
          },
        );
        if (!response) return;
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(resData.message || "Failed to update max score");
        }

        // Update local testDetails state
        setTestDetails((prev) => ({
          ...prev,
          maxScore: newMaxScore,
        }));

        swalSuccess(`Exam total marks updated to ${newMaxScore} successfully!`);
      } catch (err) {
        swalError(err.message || "Failed to update exam total marks");
      } finally {
        setLoading(false);
      }
    },
    [testId],
  );

  // Handle score mismatch detection when navigated from test creation
  useEffect(() => {
    const isScoreMismatch = Boolean(
      autoOpenSpecialInstructions ||
      location.state?.openSpecialInstructions ||
      location.state?.scoreMismatch ||
      new URLSearchParams(location.search).get("scoreMismatch") === "true"
    );

    if (isScoreMismatch && !hasHandledScoreMismatchRef.current) {
      hasHandledScoreMismatchRef.current = true;

      const initialPrompt =
        scoreMismatchDetails?.initialUserPrompt ||
        location.state?.initialUserPrompt;
      if (initialPrompt) {
        setUserPrompt(initialPrompt);
        setSpecialInstruction(initialPrompt);
      }

      const promptMaxScore =
        scoreMismatchDetails?.promptMaxScore ?? location.state?.promptMaxScore;
      const testMaxScore =
        scoreMismatchDetails?.testMaxScore ?? location.state?.testMaxScore;

      // Clean the query param from URL so refresh doesn't re-trigger
      const cleanUrl = location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);

      // Show compulsory popup message for teachers with two action options
      const presentScoreMismatchAlert = () => {
        showScoreMismatchAlert({
          testMaxScore,
          promptMaxScore,
          onEditMaxScore: () => {
            promptEditMaxScore({
              currentMaxScore: testMaxScore,
              detectedMaxScore: promptMaxScore,
              onSave: handleUpdateMaxScore,
              onCancel: presentScoreMismatchAlert,
            });
          },
          onEditInstructions: () => {
            setIsSpecialModalOpen(true);
          },
        });
      };

      presentScoreMismatchAlert();
    }
  }, [autoOpenSpecialInstructions, location, scoreMismatchDetails, handleUpdateMaxScore]);

  const handlePrintSummary = useReactToPrint({
    contentRef: summaryRef,
    documentTitle: `Performance Report - ${testInfo?.name} - ${currentAnswerPaper?.firstName} ${currentAnswerPaper?.lastName}`,
    pageStyle: `
        @page { 
          size: A4 landscape; 
          margin: 10mm;
          align: center;
        }
        @media print {
          body { 
            -webkit-print-color-adjust: exact; 
          }
        }
      `,
  });

  // Compute submission rate info dynamically
  const { submissionRate } = (() => {
    if (isManualEntryTest) {
      const total = data.length;
      const submitted = data.filter((item) => !item.isEnrollment).length;
      const rate =
        total > 0
          ? `${submitted}/${total} (${((submitted / total) * 100).toFixed(2)}%)`
          : "0/0 (0%)";
      return {
        submittedCount: submitted,
        totalEnrolled: total,
        submissionRate: rate,
      };
    }

    // Default logic for non-manual tests
    const submitted = totalRecords;
    const total = submitted + pendingSubmissionCount;
    const rate =
      total > 0
        ? `${submitted}/${total} (${((submitted / total) * 100).toFixed(2)}%)`
        : "0/0 (0%)";
    return {
      submittedCount: submitted,
      totalEnrolled: total,
      submissionRate: rate,
    };
  })();

  const updateAssessmentData = async () => {
    await fetchData(page, searchTerm, sortBy, sortOrder);
  };

  const handleGrade = async (answerPaperId) => {
    if (isManualEntryTest) {
      // For manual entry, open marks modal for single student
      const student = data.find(
        (d) =>
          d._id === answerPaperId ||
          d.studentId === answerPaperId ||
          d.answerPaperId === answerPaperId,
      );
      if (student) {
        handleOpenMarksModal([student]); // Pass as array for single
      }
      return;
    }
    // Existing logic for non-manual
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/score/grade/${answerPaperId}`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        const error = data;
        throw new Error(
          error.message || "Failed to grade student answer paper!",
        );
      }
      swalSuccess(
        data.message ||
          "Grading has been initiated successfully! You will receive an email once it is complete.",
      );
      setData((prevData) =>
        prevData.map((item) =>
          item._id === answerPaperId ? { ...item, status: "Requested" } : item,
        ),
      );
      await updateAssessmentData();
    } catch (error) {
      swalError(error.message);
    }
  };

  // Row selection handlers
  const handleRowSelect = (e, rowId) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const handleSelectAll = (visibleItemIds, areAllVisibleSelected) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (areAllVisibleSelected) {
        visibleItemIds.forEach((id) => next.delete(id));
      } else {
        visibleItemIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleOpenSingleStudentAdjust = (student) => {
    if (!student) return;
    setScoreAdjustModal({ isOpen: true, student });
  };

  const handleOpenScoreModifier = () => {
    const hasGradedInCurrent = data.some((item) => item.status === "Graded" || item.scoreId);
    const hasGradedInServer = typeof serverGradedCount === "number" && serverGradedCount > 0;
    if (!hasGradedInCurrent && !hasGradedInServer) {
      swalWarning("No graded student submissions found. Please grade submissions first before adjusting scores.");
      return;
    }
    setScoreAdjustModal({ isOpen: true, student: null });
  };

  /* const _deprecated_handleOpenScoreModifier = () => {
    if (selectedRowIds.size === 0) {
      swalWarning("Please select at least one student from the table first.");
      return;
    }
    const selectedItems = data.filter((item) => selectedRowIds.has(item._id));
    const gradedItems = selectedItems.filter(
      (item) => item.status === "Graded" || item.scoreId
    );
    if (gradedItems.length === 0) {
      swalWarning("None of the selected students have been graded yet. Please select graded students.");
      return;
    }
    setScoreModifierResults(null);
    setSelectedModifierRowIds(new Set());
    setScoreAdjustModal({ isOpen: true, student: null });
  };

  */

  const handleToggleModifierRow = (id) => {
    setSelectedModifierRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAllModifierRows = (e) => {
    if (e.target.checked) {
      if (scoreModifierResults && scoreModifierResults.length > 0) {
        setSelectedModifierRowIds(
          new Set(scoreModifierResults.map((item) => item.scoreId || item.answerPaperId).filter(Boolean))
        );
      }
    } else {
      setSelectedModifierRowIds(new Set());
    }
  };

  const handleCheckScore = async () => {
    try {
      const selectedItems = data.filter((item) => selectedRowIds.has(item._id));
      const scoreIds = selectedItems
        .filter((item) => item.status === "Graded" || item.scoreId)
        .map((item) => item.scoreId || item._id)
        .filter(Boolean);

      if (scoreIds.length === 0) {
        swalWarning("No graded score IDs found for selected students.");
        return;
      }

      setIsCheckingScore(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/score/get-grading-policy-score`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            gradingPolicy,
            strategy: gradingPolicy,
            threshold: thresholdPercentage,
            scoreIds,
          }),
        }
      );

      if (!response) {
        setIsCheckingScore(false);
        return;
      }

      const resData = await response.json();
      if (!response.ok || !resData.success) {
        throw new Error(resData.message || "Failed to calculate modified scores.");
      }

      const results = resData.data || resData.scores || [];
      setScoreModifierResults(results);
      // Automatically select all previewed students by default so user can immediately apply or deselect
      setSelectedModifierRowIds(
        new Set(results.map((item) => item.scoreId || item.answerPaperId).filter(Boolean))
      );
    } catch (error) {
      swalError(error.message || "Failed to check score modifier results.");
    } finally {
      setIsCheckingScore(false);
    }
  };

  const handleApplyGradingPolicy = async () => {
    if (!scoreModifierResults || scoreModifierResults.length === 0) {
      swalWarning("No modified scores to apply. Please click Check Score first.");
      return;
    }

    if (selectedModifierRowIds.size === 0) {
      swalWarning("Please select at least one student to apply changes.");
      return;
    }

    const selectedScoreIds = Array.from(selectedModifierRowIds);

    const result = await swalConfirm(
      `Apply ${gradingPolicy} Policy?`,
      `Are you sure you want to apply the ${gradingPolicy} policy (${Math.round(thresholdPercentage * 100)}%) to ${selectedScoreIds.length} selected student(s)? This will update their scores.`,
      "Yes, Apply Changes",
      "Cancel"
    );

    if (!result?.isConfirmed) return;

    setIsApplyingPolicy(true);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/score/apply-grading-policy`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            policyName: gradingPolicy,
            gradingPolicy,
            threshold: thresholdPercentage,
            scoreIds: selectedScoreIds,
          }),
        }
      );

      if (!response) {
        setIsApplyingPolicy(false);
        return;
      }

      const resData = await response.json();
      if (!response.ok || !resData.success) {
        throw new Error(resData.message || "Failed to apply grading policy.");
      }

      swalSuccess(
        resData.message || "Grading policy applied and student scores updated successfully!"
      );

      // Close modal and refresh the table
      setIsScoreModifierModalOpen(false);
      setScoreModifierResults(null);
      setSelectedModifierRowIds(new Set());
      await updateAssessmentData();
    } catch (error) {
      swalError(error.message || "Failed to apply grading policy.");
    } finally {
      setIsApplyingPolicy(false);
    }
  };

  const handleGradePending = async () => {
    if (isManualEntryTest) {
      // For manual entry, open marks modal for all ungraded students
      const ungradedStudents = data.filter((item) => item.status !== "Graded");
      handleOpenMarksModal(ungradedStudents);
      return;
    }
    // Existing logic for non-manual
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/score/grade-all/${testId}`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        const error = data;
        throw new Error(error.message || "Failed to grade pending students!");
      }
      swalSuccess(
        data.message ||
          "Grading pending initiated successfully! You will receive an email once grading is complete.",
      );
      setData((prevData) =>
        prevData.map((item) =>
          item.status === "Pending" ? { ...item, status: "Requested" } : item,
        ),
      );
      await updateAssessmentData();
    } catch (error) {
      swalError(error.message);
    }
  };

  // Manual Entry Functions
  const handleEditManualScore = async (item) => {
    try {
      setEditingScoreId(item._id);
      const answerPaperId = item.answerPaperId || item._id;
      let existingResult = [];

      if (answerPaperId) {
        try {
          const response = await fetchWithAuth(
            `${serverUrl}/api/score/get/${answerPaperId}`,
            {
              method: "GET",
              credentials: "include",
            },
          );
          if (response && response.ok) {
            const scoreData = await response.json();
            existingResult = scoreData?.score?.result || [];
          }
        } catch (fetchErr) {
          console.error("Failed to fetch existing score for editing:", fetchErr);
        }
      }

      const computedName =
        item.studentName ||
        `${item.firstName || ""} ${item.lastName || ""}`.trim() ||
        "Unnamed Student";

      const entries = tasks.map((task) => {
        const qNum = String(task.questionNumber);
        const matched = existingResult.find(
          (r) => String(r.questionNumber) === qNum
        );
        return {
          marksObtained:
            matched !== undefined && matched.obtainedScore !== undefined
              ? matched.obtainedScore
              : "",
          feedback: matched ? matched.feedback || "" : "",
        };
      });

      const studentMarksData = [
        {
          studentId: item.studentId || item._id,
          studentName: computedName,
          rollNo: item.rollNo || item.referenceId || "",
          entries,
        },
      ];

      setAllStudentsMarks(studentMarksData);
      setModifiedStudents(new Set([0])); // Mark as modified so Save will commit updates
      setIsMarksModalOpen(true);
    } catch (err) {
      console.error("Error opening edit score modal:", err);
      swalError("Failed to load student score for editing.");
    } finally {
      setEditingScoreId(null);
    }
  };

  const handleOpenMarksModal = (students) => {
    const studentsMarksData = students.map((student) => {
      const computedName =
        student.studentName ||
        `${student.firstName || ""} ${student.lastName || ""}`.trim() ||
        "Unnamed Student";
      return {
        studentId: student.studentId || student._id,
        studentName: computedName,
        rollNo: student.rollNo || student.referenceId || "",
        entries: tasks.map(() => ({ marksObtained: "", feedback: "" })), // Initialize with empty string for '-'
      };
    });

    setAllStudentsMarks(studentsMarksData);
    setModifiedStudents(new Set()); // Reset modified students
    setIsMarksModalOpen(true);
  };

  const handleDirectDownloadTemplate = () => {
    generateAndDownloadTemplate({
      students: allStudentsMarks,
      tasks,
      testName: testInfo?.name || "Manual_Entry_Exam",
    });
  };

  const handleApplyAutofill = (matchedUpdates) => {
    const updatedIndices = Object.keys(matchedUpdates).map(Number);
    if (updatedIndices.length === 0) return;

    setAllStudentsMarks((prev) =>
      prev.map((student, sIdx) => {
        const studentUpdates = matchedUpdates[sIdx];
        if (!studentUpdates) return student;

        const newEntries = student.entries.map((entry, tIdx) => {
          const update = studentUpdates[tIdx];
          if (!update) return entry;
          return {
            ...entry,
            ...(update.marksObtained !== undefined
              ? { marksObtained: update.marksObtained }
              : {}),
            ...(update.feedback !== undefined
              ? { feedback: update.feedback }
              : {}),
          };
        });

        return {
          ...student,
          entries: newEntries,
        };
      })
    );

    // Add all updated student indices to modifiedStudents set so they will be saved
    setModifiedStudents((prev) => {
      const next = new Set(prev);
      updatedIndices.forEach((idx) => next.add(idx));
      return next;
    });
  };

  const handleRowChange = (rowIndex, field, value) => {
    const taskIndex = parseInt(field.replace("task", ""));

    if (value === "" || value === null || value === undefined) {
      setAllStudentsMarks((prev) =>
        prev.map((student, i) =>
          i === rowIndex
            ? {
                ...student,
                entries: student.entries.map((entry, j) =>
                  j === taskIndex ? { ...entry, marksObtained: "" } : entry,
                ),
              }
            : student,
        ),
      );
      // Do not mark as modified when clearing to empty
      return;
    }

    const numValue = parseFloat(value);

    if (isNaN(numValue)) {
      return;
    }

    // Ensure no more than 2 decimal places
    const decimalPart = value.split(".")[1];
    if (decimalPart && decimalPart.length > 2) {
      return;
    }

    setAllStudentsMarks((prev) =>
      prev.map((student, i) =>
        i === rowIndex
          ? {
              ...student,
              entries: student.entries.map((entry, j) =>
                j === taskIndex ? { ...entry, marksObtained: numValue } : entry,
              ),
            }
          : student,
      ),
    );
    setModifiedStudents((prev) => new Set([...prev, rowIndex]));
  };

  const handleSaveAllMarks = async (e) => {
    e.preventDefault();

    setIsSaving(true);
    try {
      // Only send data for modified students
      const modifiedIndices = Array.from(modifiedStudents);
      const manualSubmissions = modifiedIndices.map((index) => {
        const student = allStudentsMarks[index];
        return {
          studentId: student.studentId,
          result: student.entries.map((entry, taskIndex) => ({
            questionNumber: tasks[taskIndex].questionNumber.toString(),
            obtainedScore: entry.marksObtained || 0,
            feedback: entry.feedback || "",
          })),
        };
      });

      if (manualSubmissions.length === 0) {
        swalWarning("No changes to save.");
        setIsSaving(false);
        return;
      }

      // Check if any modified student has a score exceeding maxScore
      const exceededList = [];
      modifiedIndices.forEach((index) => {
        const student = allStudentsMarks[index];
        student.entries.forEach((entry, taskIndex) => {
          const v = parseFloat(entry.marksObtained);
          const max = tasks[taskIndex]?.maxScore || 0;
          if (!isNaN(v) && v > max) {
            exceededList.push(
              `${student.studentName}: Maximum score you can give is up to ${max} for this test.`
            );
          }
        });
      });

      if (exceededList.length > 0) {
        swalError(
          "Cannot save marks. Some student scores exceed the maximum allowed score. Please correct the entries highlighted in red before saving."
        );
        setIsSaving(false);
        return;
      }

      const response = await fetchWithAuth(
        `${serverUrl}/api/score/manual-entry/test/${testId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ manualSubmissions }),
          credentials: "include",
        },
      );

      if (!response || !response.ok) {
        throw new Error("Failed to save marks");
      }

      swalSuccess(
        "Marks saved successfully.",
      );
      setIsMarksModalOpen(false);
      await updateAssessmentData();
    } catch (err) {
      swalError(err.message || "Failed to save marks");
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenFeedbackModal = (studentIndex, taskIndex) => {
    const currentFeedback =
      allStudentsMarks[studentIndex]?.entries[taskIndex]?.feedback || "";
    setFeedbackModal({
      isOpen: true,
      studentIndex,
      taskIndex,
      feedback: currentFeedback,
    });
  };

  const handleSaveFeedback = () => {
    const { studentIndex, taskIndex, feedback } = feedbackModal;

    setAllStudentsMarks((prev) =>
      prev.map((student, i) =>
        i === studentIndex
          ? {
              ...student,
              entries: student.entries.map((entry, j) =>
                j === taskIndex ? { ...entry, feedback } : entry,
              ),
            }
          : student,
      ),
    );

    setFeedbackModal({
      isOpen: false,
      studentIndex: null,
      taskIndex: null,
      feedback: "",
    });
  };

  const getManualEntryHeaders = () => {
    return [
      {
        key: "studentName",
        label: "Student Name",
        width: "1.2fr",
        required: false,
      },
      {
        key: "rollNo",
        label: "Roll No",
        width: "0.8fr",
        required: false,
      },
      ...tasks.map((task, index) => ({
        key: `task${index}`,
        label: `${task.questionNumber} (${task.maxScore})`,
        width: "1fr",
        required: true,
        title: task.questionText,
      })),
      {
        key: "totalScore",
        label: "Total Score",
        width: "1fr",
        required: false,
      },
    ];
  };

  const getManualEntryRows = () => {
    return allStudentsMarks.map((student) => {
      const taskFields = student.entries.reduce(
        (acc, entry, index) => ({
          ...acc,
          [`task${index}`]:
            entry.marksObtained === "" ? "-" : entry.marksObtained || 0,
        }),
        {},
      );

      const total = student.entries.reduce((sum, entry) => {
        const v = parseFloat(entry.marksObtained);
        return sum + (isNaN(v) ? 0 : v);
      }, 0);

      return {
        studentName: student.studentName,
        rollNo: student.rollNo || "-",
        ...taskFields,
        totalScore: Number.isFinite(total) ? total : 0,
      };
    });
  };

  const renderManualEntryCell = (header, row, rowIndex) => {
    if (header.key === "studentName") {
      return (
        <span style={{ fontWeight: "600", fontSize: "0.95rem" }}>
          {row.studentName}
        </span>
      );
    }

    if (header.key === "rollNo") {
      return (
        <span style={{ color: "#64748b", fontSize: "0.9rem" }}>
          {row.rollNo || "-"}
        </span>
      );
    }

    // Render Total Score cell (read-only, updates in real-time via allStudentsMarks)
    if (header.key === "totalScore") {
      const total = allStudentsMarks[rowIndex]?.entries.reduce((sum, entry) => {
        const v = parseFloat(entry.marksObtained);
        return sum + (isNaN(v) ? 0 : v);
      }, 0);
      return (
        <span style={{ fontWeight: "700", fontSize: "0.95rem" }}>
          {Number.isFinite(total) ? total : 0} /{" "}
          {tasks.reduce((sum, task) => sum + (task.maxScore || 0), 0)}
        </span>
      );
    }

    // task cells
    const taskIndex = parseInt(header.key.replace("task", ""), 10);
    const maxMarks = tasks[taskIndex]?.maxScore || 0;
    const currentValue =
      allStudentsMarks[rowIndex]?.entries[taskIndex]?.marksObtained;
    const hasFeedback =
      allStudentsMarks[rowIndex]?.entries[taskIndex]?.feedback;

    const parsedScore = parseFloat(currentValue);
    const isExceeded = !isNaN(parsedScore) && parsedScore > maxMarks;

    return (
      <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: "0.25rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <input
            type="number"
            value={
              currentValue === "" ||
              currentValue === null ||
              currentValue === undefined
                ? ""
                : currentValue
            }
            onChange={(e) =>
              handleRowChange(rowIndex, header.key, e.target.value)
            }
            className={`modal-table-input ${isExceeded ? "has-error" : ""}`}
            placeholder={header.placeholder}
            min="0"
            step="0.1"
            disabled={isSaving}
            style={{
              flex: 1,
              border: isExceeded ? "2px solid #ef4444" : undefined,
              backgroundColor: isExceeded ? "#fef2f2" : undefined,
              color: isExceeded ? "#dc2626" : undefined,
              fontWeight: isExceeded ? "600" : undefined,
              borderRadius: "6px",
              padding: "0.45rem 0.6rem",
            }}
          />
          <button
            type="button"
            onClick={() => handleOpenFeedbackModal(rowIndex, taskIndex)}
            className="feedback-icon-btn"
            title={hasFeedback ? `Feedback: ${hasFeedback}` : "Add feedback"}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "0.25rem",
              color: hasFeedback ? "var(--color-1)" : "#999",
              fontSize: "1.2rem",
            }}
          >
            <i
              className={`fa-${
                hasFeedback ? "solid" : "regular"
              } fa-comment-dots`}
            ></i>
          </button>
        </div>
        {isExceeded && (
          <span
            style={{
              color: "#ef4444",
              fontSize: "0.75rem",
              fontWeight: "600",
              lineHeight: "1.2",
              textAlign: "left",
            }}
          >
            Maximum score you can give is up to {maxMarks} for this test.
          </span>
        )}
      </div>
    );
  };

  const handleRegrade = async (answerPaperId) => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/score/grade/${answerPaperId}`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        const error = data;
        throw new Error(error.message || "Failed to regrade student!");
      }
      swalSuccess(
        "Regrade initiated successfully! You will receive an email once grading is complete.",
      );
      setData((prevData) =>
        prevData.map((item) =>
          item._id === answerPaperId ? { ...item, status: "Requested" } : item,
        ),
      );
      await updateAssessmentData();
    } catch (error) {
      swalError(error.message);
    }
  };

  // Handler for Manual Review button - checks if any question needs manual review
  const handleManualReview = async (answerPaperId) => {
    try {
      // Fetch score data to check for isManualReview questions
      const response = await fetchWithAuth(
        `${serverUrl}/api/score/get/${answerPaperId}`,
        {
          method: "GET",
          credentials: "include",
        },
      );

      if (!response) {
        // Fallback to navigation without ?mr=true
        navigate(`/instructor/results/${answerPaperId}`);
        return;
      }

      const data = await response.json();

      if (!response.ok || !data.score?.result) {
        // Fallback to navigation without ?mr=true
        navigate(`/instructor/results/${answerPaperId}`);
        return;
      }

      // Check if any question has isManualReview=true
      const hasManualReviewQuestion = data.score.result.some(
        (q) => q.isManualReview === true,
      );

      if (hasManualReviewQuestion) {
        navigate(`/instructor/results/${answerPaperId}?mr=true`);
      } else {
        // Navigate without ?mr=true, swalWarning will be shown on ViewResults
        navigate(`/instructor/results/${answerPaperId}`);
      }
    } catch (error) {
      // On error, still navigate to results page
      navigate(`/instructor/results/${answerPaperId}`);
    }
  };

  const handleInitiateSummarize = async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/score/initiate-summarize-all/${testId}`,
        {
          method: "POST",
          credentials: "include",
        },
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        const error = data;
        throw new Error(
          error.message || "Failed to initiate all scores summarization!",
        );
      }
      swalSuccess(
        data.message ||
          "Scores summarization initiated successfully! You will receive an email once process is complete.",
      );
      setData((prevData) =>
        prevData.map((item) =>
          item.summaryStatus === "Pending"
            ? { ...item, summaryStatus: "Requested" }
            : item,
        ),
      );
      await updateAssessmentData();
    } catch (error) {
      swalError(error.message);
    }
  };

  const gradedCount = isManualEntryTest
    ? data.filter((item) => item.status === "Graded").length
    : serverGradedCount;
  const submissionCount = isManualEntryTest
    ? data.length
    : overallTotalRecords;

  const handleSortPending = () => {
    const sortedData = [...data].sort((a, b) => {
      if (a.status === "Pending" && b.status !== "Pending") return -1;
      if (a.status !== "Pending" && b.status === "Pending") return 1;
      return 0;
    });
    setData(sortedData);
  };

  const handleGenerateUserPromptAI = async () => {
    try {
      setAiLoading(true);
      // keep a copy so Undo can restore
      setAiPrevPrompt(specialInstruction);
      const response = await fetchWithAuth(
        `${serverUrl}/api/test/generate-userPrompt/${testDetails._id}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userTypedPrompt: specialInstruction || "" }),
        },
      );
      if (!response) {
        setAiLoading(false);
        return;
      }
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json.message || "AI generation failed");
      }

      // try common response shapes
      const generated =
        json.userPrompt ||
        json.generatedPrompt ||
        json.data?.userPrompt ||
        json.prompt ||
        (typeof json === "string" ? json : "") ||
        "";

      if (!generated) {
        throw new Error("AI returned empty prompt");
      }

      // overwrite textarea and show Accept / Undo
      setSpecialInstruction(generated);
      setAiHasActions(true);
      setAiLoading(false);
    } catch (err) {
      setAiLoading(false);
      swalError(err.message || "AI generation failed");
    }
  };

  const handleAiAccept = () => {
    // keep generated value as current textarea, clear undo state
    setAiPrevPrompt(null);
    setAiHasActions(false);
  };

  const handleAiUndo = () => {
    if (aiPrevPrompt != null) {
      setSpecialInstruction(aiPrevPrompt);
    }
    setAiPrevPrompt(null);
    setAiHasActions(false);
  };

  const handleNotifyStudents = async (withAnswerPaper = false) => {
    try {
      setPublishWithAnswerPaper(withAnswerPaper);
      // show progress immediately (publishResults also sets these but doing it here avoids a flicker)
      setIsPublishing(true);
      setPublishPhase("preparing");
      setPublishProgress(
        "Reports are being generated... Notifying students. This might take a while. Please don't close this page.",
      );
      publishResults();
    } catch (err) {
      console.error("handleNotifyStudents error", err);
      swalError("Failed to start notification process.");
    }
  };

  const handleDownloadAllReports = async () => {
    setIsPublishing(true);
    setPublishPhase("preparing");
    setPublishProgress("Preparing download process...");
    try {
      const now = new Date();
      const timezoneOffset = now.getTimezoneOffset();
      const timezoneOffsetHours = -timezoneOffset / 60;

      await fetchWithAuth(
        `${serverUrl}/api/test/update/${testId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            scorePublishedDate: new Date().toISOString(),
            timezoneOffset: timezoneOffsetHours,
          }),
        },
      );

      setTestDetails((prev) => ({
        ...prev,
        scorePublishedDate: new Date().toISOString(),
      }));

      setPublishPhase("generating");
      setPublishProgress("Compiling consolidated PDF report on server...");

      const resp = await fetchWithAuth(
        `${serverUrl}/api/score/generate-bulk-report/${testId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            type: "combined",
            includeAnswerPaper: publishWithAnswerPaper,
          }),
        },
      );

      if (!resp || !resp.ok) {
        const errData = await resp?.json().catch(() => ({}));
        throw new Error(errData?.message || "Failed to generate bulk reports PDF.");
      }

      setPublishPhase("downloading");
      setPublishProgress("Downloading file...");

      const blob = await resp.blob();
      const safeTestName = String(testDetails?.name || testInfo?.name || "Assessment").replace(/[/\\?%*:|"<>]/g, "_");
      const filename = `Bulk_Reports_${safeTestName}.pdf`;

      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 5000);

      setPublishPhase("completed");
      setPublishProgress("Download completed successfully!");
      swalSuccess("Success!", "Consolidated PDF report downloaded successfully.");
    } catch (err) {
      console.error("handleDownloadAllReports error", err);
      swalError(err.message || "Failed to generate bulk reports.");
    } finally {
      setIsPublishing(false);
      setPublishPhase("");
      setPublishProgress("");
    }
  };

  const handleJustPublish = async () => {
    setIsPublishing(true);
    setPublishPhase("preparing");
    setPublishProgress("Publishing results...");

    try {
      const now = new Date();
      const timezoneOffset = now.getTimezoneOffset();
      const timezoneOffsetHours = -timezoneOffset / 60;

      const updateResp = await fetchWithAuth(
        `${serverUrl}/api/test/update/${testId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            scorePublishedDate: new Date().toISOString(),
            timezoneOffset: timezoneOffsetHours,
          }),
        },
      );

      if (!updateResp || !updateResp.ok) {
        throw new Error("Failed to publish results");
      }

      // Optimistic UI update
      setTestDetails((prev) => ({
        ...prev,
        scorePublishedDate: new Date().toISOString(),
      }));

      setPublishPhase("completed");
      setPublishProgress("Results published successfully!");

      // Small delay to show completion message
      await new Promise((resolve) => setTimeout(resolve, 1500));

      swalSuccess(
        "Results published successfully! Students can now view their scores in the GradeMe AI App.",
      );
      setIsPublishModalOpen(false);

      // Refresh data to reflect published status
      await updateAssessmentData();
    } catch (error) {
      console.error("handleJustPublish error", error);
      swalError(error.message || "Failed to publish results!");
    } finally {
      setLoading(false);
      setIsPublishing(false);
      setPublishPhase("");
      setPublishProgress("");
    }
  };

  // Notify all graded students via WhatsApp or Email
  const handleNotifyAllStudents = async (notificationType) => {


    const channelLabel =
      notificationType === "whatsapp"
        ? "WhatsApp messages"
        : notificationType === "push"
        ? "push notifications"
        : notificationType === "all"
        ? "notifications (Email, WhatsApp & Push)"
        : "emails";

    const channelTitle =
      notificationType === "whatsapp"
        ? "WhatsApp"
        : notificationType === "push"
        ? "Push Notification"
        : notificationType === "all"
        ? "All Channels"
        : "Email";

    const confirmResult = await swalConfirm(
      `${channelTitle} All Students?`,
      `Are you sure you want to send ${channelLabel} to all graded students?\n\nInclude original answer paper in notifications?`,
      "Yes, with Answer Paper",
      "Yes, without Answer Paper",
      true,
    );

    let includeAnswerPaper = false;
    if (!confirmResult) return;

    if (confirmResult.isConfirmed) {
      includeAnswerPaper = true;
    } else if (confirmResult.dismiss === "cancel") {
      includeAnswerPaper = false;
    } else {
      return;
    }

    setIsPublishing(true);
    setPublishPhase("preparing");
    setPublishProgress(`Preparing to send ${channelTitle} notifications...`);

    try {
      let allSubmissions = data;
      if (!isManualEntryTest) {
        try {
          const resp = await fetchWithAuth(
            `${serverUrl}/api/answer-paper/get-all/${testId}?all=true`,
            { method: "GET", credentials: "include" }
          );
          if (resp && resp.ok) {
            const raw = await resp.json();
            allSubmissions = (Array.isArray(raw) ? raw : (raw.answerPapers || [])).map((item) => ({
              ...item,
              studentName: `${item.firstName || ""} ${item.lastName || ""}`.trim(),
              status: item.status || "Pending",
            }));
          }
        } catch (err) {
          console.warn("Failed to fetch all submissions for notification:", err);
        }
      }

      const gradedStudents = allSubmissions.filter((d) => d.status === "Graded");
      if (gradedStudents.length === 0) {
        swalWarning("No graded submissions to notify.");
        return;
      }

      let count = 0;
      for (const student of gradedStudents) {
        count++;
        const studentName = student.studentName || `${student.firstName || ""} ${student.lastName || ""}`.trim() || student._id;
        setCurrentStudentName(studentName);
        setPublishPhase("sending");
        setPublishProgress(`Sending notification to ${studentName} (${count}/${gradedStudents.length})...`);

        try {
          await fetchWithAuth(
            `${serverUrl}/api/answer-paper/notify-student/${student._id}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                notificationType: String(notificationType || "").toLowerCase(),
                includeAnswerPaper,
              }),
            },
          );
        } catch (innerErr) {
          console.error("Error notifying student", student._id, innerErr);
        }
      }

      setPublishPhase("completed");
      setPublishProgress("All notifications sent successfully!");
      setCurrentStudentName("");
      swalSuccess(`${channelTitle} notifications queued for all graded students.`);
      await updateAssessmentData();
    } catch (error) {
      console.error("handleNotifyAllStudents error", error);
      swalError(`Failed to send ${notificationType} notifications!`);
    } finally {
      setLoading(false);
      setIsPublishing(false);
      setPublishPhase("");
      setPublishProgress("");
      setCurrentStudentName("");
    }
  };

  const calculateAverage = () => {
    const gradedData = data.filter((item) => item.status === "Graded");
    if (gradedData.length === 0) return 0;
    const total = gradedData.reduce(
      (acc, item) => acc + (item.totalScore || 0),
      0,
    );
    return (total / gradedData.length).toFixed(2);
  };

  const calculateStandardDeviation = () => {
    const gradedData = data.filter((item) => item.status === "Graded");
    if (gradedData.length === 0) return 0;
    const avg = calculateAverage();
    const variance =
      gradedData.reduce(
        (acc, item) => acc + Math.pow((item.totalScore || 0) - avg, 2),
        0,
      ) / gradedData.length;
    return Math.sqrt(variance).toFixed(2);
  };

  const averageScore = calculateAverage();
  const standardDeviation = calculateStandardDeviation();

  const columns = (() => {
    const showNotifyColumns = data.some((d) => d.status === "Graded");

    let base = [
      {
        key: "studentName",
        label: "Student Name",
        render: (item) => (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "0.5rem" }}>
            <span>{item.studentName}</span>
            {item.proctoringLog?.violations?.length > 0 && (
              <i 
                className="fa-solid fa-circle-exclamation" 
                style={{ color: "#ef4444" }} 
                title={`${item.proctoringLog.violations.length} Violation(s) Detected`}
              ></i>
            )}
          </div>
        ),
        textAlign:"left"
      },
      {
        key: "referenceId",
        label: "ID",
      },
      {
        key: "rollNo",
        label: "Roll No",
        render: (item) => item.rollNo || "-",
      },
    ];

    // For ManualEntryTest, show Assessment column only if url is present

    !isManualEntryTest && 
    base.push({
      key: "answerpaper",
      label: "Assessment",
      render: (item) => {
        if (!item.url) return <span>-</span>;
        const handlePreview = () => {
          window.open(item.url, "_blank");
        };
        return (
          <button className="preview-btn" onClick={handlePreview}>
            <img src="/imgs/eye_icon.svg" alt="preview-btn" width={30} />
          </button>
        );
      },
      sortable: false,
    });

    // Add Score column
    if (!isHomework) {
      base.push(
        {
          key: "totalScore",
          label: "Score",
          headerStyle: { minWidth: "120px" },
          cellStyle: { minWidth: "120px" },
        render: (item) => {
          let scoreContent = null;
          if (item.status === "Graded" || item.status === "Review") {
            // For manual entry tests, we need to find the answer paper ID
            const answerPaperId = isManualEntryTest
              ? item.answerPaperId || item._id
              : item._id;

            scoreContent = (
              !(isManualEntryTest && instituteInfo?.category === "K12_SCHOOL") ? (
              <a
                href={`/instructor/results/${answerPaperId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="score-clickable"
                title="View Results"
              >
                {item.totalScore}
              </a>
              ): (
                <span
                title="View Results"
              >
                {item.totalScore}
              </span>
              )
            );
          } else {
            scoreContent = <span>-</span>;
          }
          const canAdjust = (item.status === "Graded" || item.status === "Review") && !isManualEntryTest;
          const adjustBtn = canAdjust ? (
            <button
              type="button"
              className="row-score-adjust-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOpenSingleStudentAdjust(item);
              }}
              style={{
                margin: 0,
                border: "1px solid #e5e7eb",
                background: "#f9fafb",
                borderRadius: "5px",
                padding: "2px 5px",
                cursor: "pointer",
                color: "#f09a21",
                fontSize: "0.72rem",
                transition: "all 0.15s ease",
                display: "inline-flex",
                alignItems: "center",
                verticalAlign: "middle",
              }}
              title={`Adjust score for ${item.studentName || "student"}`}
              aria-label={`Adjust score for ${item.studentName || "student"}`}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#fff7ed";
                e.currentTarget.style.borderColor = "#f59e0b";
                e.currentTarget.style.color = "#d97706";
                e.currentTarget.style.boxShadow = "0 1px 3px rgba(245, 158, 11, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#f9fafb";
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.color = "#f09a21";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <i className="fa-solid fa-sliders"></i>
            </button>
          ) : null;

          if (user?.sourceSystem === "Google Classroom") {
            return (
              <div className="score-actions">
                {scoreContent}
                {adjustBtn}
                <button
                  className="copy-btn"
                  onClick={() => copyScore(item.totalScore)}
                  title="Copy Score"
                >
                  <i className="fa-regular fa-clipboard"></i>
                </button>
                <button
                  className="open-gc-btn"
                  onClick={() => openGoogleClassroom(item._id)}
                  title="Open Google Classroom"
                >
                  <i className="fa-solid fa-arrow-up-right-from-square"></i>
                </button>
              </div>
            );
          }
          return (
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                minHeight: "26px",
                paddingLeft: "10px",
                paddingRight: "38px",
                boxSizing: "border-box",
              }}
            >
              <div style={{ textAlign: "center", whiteSpace: "nowrap" }}>
              {scoreContent}
              </div>
              {adjustBtn && (
                <div
                  style={{
                    position: "absolute",
                    right: "4px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  {adjustBtn}
                </div>
              )}
            </div>
          );
        },
      }
    );
    }

    // Add Performance column only for AI Grading tests
    if (!isManualEntryTest && !isHomework) {
      base.push({
        key: "performanceSummary",
        label: "Performance",
        render: (item) => {
          return (
            <>
              {item.status === "Graded" || item.status === "Review" ? (
                item.summaryStatus === "Summarized" ? (
                  <button
                    className="preview-btn"
                    onClick={() => handleViewSummary(item)}
                  >
                    <img
                      src="/imgs/eye_icon.svg"
                      alt="preview-btn"
                      width={30}
                    />
                  </button>
                ) : item.summaryStatus === "Requested" ? (
                  <button
                    className="grading-in-progress-btn"
                    disabled
                    style={{
                      backgroundColor: "#ff8c00",
                      color: "#fff",
                      borderColor: "#ff8c00",
                      cursor: "default",
                    }}
                  >
                    Requested
                  </button>
                ) : (
                  <button className="grading-in-progress-btn" disabled>
                    Pending
                  </button>
                )
              ) : (
                <span>-</span>
              )}
            </>
          );
        },
        sortable: false,
      });
    }

    // Action column - for manual entry tests, change button text
    if (!isManualEntryTest) {
      base.push({
        key: "action",
        label: "Action",
        render: (item) => {
          const status = (item.status || "").toLowerCase();

          if (status === "pending") {
            return (
              <button
                className="grade-btn"
                onClick={() => handleGrade(item._id)}
              >
                <i className="fa-regular fa-square-check"></i> GradeMe
              </button>
            );
          } else if (status === "requested") {
            return (
              <button className="grading-in-progress-btn" disabled>
                Grading in Progress
              </button>
            );
          } else if (status === "review") {
            return (
              <>
                <button
                  className="mr-btn"
                  onClick={() => handleManualReview(item._id)}
                >
                  <i className="fa-solid fa-user"></i> Manual Review
                </button>
                <button
                  className="regrade-btn"
                  style={{ marginLeft: ".3rem" }}
                  onClick={() => handleRegrade(item._id)}
                >
                  <i className="fa-solid fa-repeat"></i> Regrade
                </button>
              </>
            );
          } else if (status === "graded") {
            return (
              <button className="regrade-btn" style={{ cursor: "not-allowed" }}>
                Graded <i className="fa-regular fa-square-check"></i>
              </button>
            );
          } else {
            return null;
          }
        },
        sortable: false,
      });
    } else {
      // For manual entry, show Enter Marks button
      base.push({
        key: "action",
        label: "Action",
        render: (item) => {
          const status = (item.status || "").toLowerCase();

          if (status === "pending") {
            return (
              <button
                className="grade-btn"
                onClick={() => handleGrade(item._id)}
              >
                <i className="fa-regular fa-square-check"></i> GradeMe
              </button>
            );
          } else if (status === "graded") {
            return (
              <button
                className="regrade-btn"
                onClick={() => handleEditManualScore(item)}
                title="Click to edit score"
                style={{ cursor: "pointer" }}
              >
                Graded <i className="fa-solid fa-pen" style={{ marginLeft: "4px" }}></i>
              </button>
            );
          } else {
            return null;
          }
        },
        sortable: false,
      });
    }

    // show notify/download columns as soon as at least one paper is graded
    if (showNotifyColumns && !isManualEntryTest) {
      base.push(
        {
          key: "whatsapp",
          label: "WhatsApp",
          render: (item) => {
            if (item.status !== "Graded") return <span>-</span>;

            const isWhatsAppNotEnabled = item.receiveWhatsAppMessages === false;

            return (
              <div style={{ position: "relative", display: "inline-block" }}>
                <button
                  className="delete-btn"
                  disabled={
                    isWhatsAppNotEnabled ||
                    loading ||
                    (activeAction.id === item._id &&
                      activeAction.type === "WhatsApp")
                  }
                  onClick={() => handleNotifySingle(item._id, "WhatsApp")}
                  title={
                    isWhatsAppNotEnabled
                      ? undefined
                      : activeAction.id === item._id &&
                        activeAction.type === "WhatsApp"
                        ? "Working..."
                        : "Notify via WhatsApp"
                  }
                  style={{
                    backgroundColor: "#25D366",
                    color: "#fff",
                    cursor: isWhatsAppNotEnabled ? "not-allowed" : undefined,
                    opacity: isWhatsAppNotEnabled ? 0.5 : undefined,
                  }}
                >
                  {activeAction.id === item._id &&
                  activeAction.type === "WhatsApp" ? (
                    <Loader style={{ left: "0" }} />
                  ) : (
                    <i
                      className="fa-brands fa-whatsapp"
                      style={{ fontSize: "1.5rem", color: "#fff" }}
                    ></i>
                  )}
                </button>
                {isWhatsAppNotEnabled && (
                  <span
                    className="notify-denied-tooltip"
                    style={{
                      position: "absolute",
                      bottom: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: "#1f2937",
                      color: "#fff",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "0.75rem",
                      whiteSpace: "nowrap",
                      pointerEvents: "none",
                      opacity: 0,
                      transition: "opacity 0.2s",
                      zIndex: 10,
                      marginBottom: "4px",
                    }}
                  >
                    WhatsApp Not Enabled
                    <span
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        borderWidth: "4px",
                        borderStyle: "solid",
                        borderColor: "#1f2937 transparent transparent transparent",
                      }}
                    />
                  </span>
                )}
              </div>
            );
          },
          sortable: false,
        },
        {
          key: "email",
          label: "Email",
          render: (item) => {
            if (item.status !== "Graded") return <span>-</span>;

            const isEmailNotEnabled = item.receiveEmailMessages === false;

            return (
              <div style={{ position: "relative", display: "inline-block" }}>
                <button
                  className="delete-btn"
                  disabled={
                    isEmailNotEnabled ||
                    loading ||
                    (activeAction.id === item._id &&
                      activeAction.type === "email")
                  }
                  onClick={() => handleNotifySingle(item._id, "email")}
                  title={
                    isEmailNotEnabled
                      ? undefined
                      : activeAction.id === item._id && activeAction.type === "email"
                        ? "Working..."
                        : "Notify via Email"
                  }
                  style={isEmailNotEnabled ? { cursor: "not-allowed", opacity: 0.5 } : undefined}
                >
                  {activeAction.id === item._id &&
                  activeAction.type === "email" ? (
                    <Loader style={{ left: "0" }} />
                  ) : (
                    <i
                      className="fa-regular fa-envelope"
                      style={{ fontSize: "1.5rem" }}
                    ></i>
                  )}
                </button>
                {isEmailNotEnabled && (
                  <span
                    className="notify-denied-tooltip"
                    style={{
                      position: "absolute",
                      bottom: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: "#1f2937",
                      color: "#fff",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "0.75rem",
                      whiteSpace: "nowrap",
                      pointerEvents: "none",
                      opacity: 0,
                      transition: "opacity 0.2s",
                      zIndex: 10,
                      marginBottom: "4px",
                    }}
                  >
                    Email Not Enabled
                    <span
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        borderWidth: "4px",
                        borderStyle: "solid",
                        borderColor: "#1f2937 transparent transparent transparent",
                      }}
                    />
                  </span>
                )}
              </div>
            );
          },
          sortable: false,
        },
        {
          key: "push",
          label: "Push",
          render: (item) => {
            if (item.status !== "Graded") return <span>-</span>;

            return (
              <button
                className="delete-btn"
                disabled={
                  loading ||
                  (activeAction.id === item._id && activeAction.type === "push")
                }
                onClick={() => handleNotifySingle(item._id, "push")}
                title={
                  activeAction.id === item._id && activeAction.type === "push"
                    ? "Working..."
                    : "Notify via Push"
                }
                style={{
                  backgroundColor: "#f59e0b",
                  color: "#fff",
                }}
              >
                {activeAction.id === item._id && activeAction.type === "push" ? (
                  <Loader style={{ left: "0" }} />
                ) : (
                  <i
                    className="fa-solid fa-bell"
                    style={{ fontSize: "1.5rem", color: "#fff" }}
                  ></i>
                )}
              </button>
            );
          },
          sortable: false,
        },
        {
          key: "download",
          label: "Download",
          render: (item) => {
            if (item.status !== "Graded" && item.status !== "Review") return <span>-</span>;

            const hasSummary = Boolean(
              item.summary ||
              (item.summaryStatus === "Summarized" && item.summary) ||
              (item.summary?.overallPerformance)
            );

            const studentName =
              item.studentName ||
              `${item.firstName || ""} ${item.lastName || ""}`.trim() ||
              item._id;

            return (
              <SmartDownloadButton
                answerPaperId={item._id}
                feedbackMode={instituteInfo?.feedbackMode || "APP_FEEDBACK_ONLY"}
                hasSummary={hasSummary}
                studentName={studentName}
                referenceId={item.referenceId || item.rollNo || item.studentId || item._id}
                testName={testDetails?.name || testInfo?.name || "Assessment"}
                disabled={loading || activeAction.id === item._id}
                serverUrl={serverUrl}
                fetchWithAuth={fetchWithAuth}
                swalError={swalError}
                swalSuccess={swalSuccess}
              />
            );
          },
          sortable: false,
        },
      );
    }

    // Only show Delete column when it's NOT a manual entry test
    base.push({
      key: "delete",
      label: "Actions",
      render: (item) => {
        // don't show Delete button for enrollment-only rows (no answer-paper)
        if (!item._id) return <span>-</span>;
        // don't show Delete button for enrollment-only rows (no answer-paper)
        if (item.isEnrollment) return <span>-</span>;

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
            }}
          >
            {isManualEntryTest && (
              <button
                className="delete-btn"
                onClick={() => handleEditManualScore(item)}
                title="Edit Score"
                disabled={editingScoreId === item._id}
              >
                {editingScoreId === item._id ? (
                  <Loader style={{ left: "0" }} />
                ) : (
                  <img src="/imgs/pencil_icon.svg" alt="edit-btn" width={20} />
                )}
              </button>
            )}
            <button
              className="delete-btn"
              disabled={
                activeAction.id === item._id && activeAction.type === "delete"
              }
              onClick={() => handleDeleteSubmission(item._id)}
              title="Delete submission"
            >
              {activeAction.id === item._id && activeAction.type === "delete" ? (
                <Loader style={{ left: "0" }} />
              ) : (
                <img src="/imgs/trash_icon.svg" alt="delete-btn" width={20} />
              )}
            </button>
          </div>
        );
      },
      sortable: false,
    });

    return base;
  })();

  const handleViewSummary = async (answerPaper) => {
    if (!answerPaper.scoreId) return;
    setIsSummaryModalOpen(true);
    setCurrentAnswerPaper(answerPaper);
  };

  // Open export modal to let user choose export type
  const openExportModal = () => {
    setExportType(null);
    setExportProgress({ step: 0, message: "" });
    setIsExportInProgress(false);
    setIsExportModalOpen(true);
  };

  // Summarized export - quick CSV with total scores only
  const downloadSummarizedExport = async () => {
    setIsExportInProgress(true);
    setExportProgress({ step: 1, message: "Gathering student list..." });

    try {
      await new Promise((r) => setTimeout(r, 300)); // Small delay for UX

      let allSubmissions = data;
      if (!isManualEntryTest) {
        try {
          const resp = await fetchWithAuth(
            `${serverUrl}/api/answer-paper/get-all/${testId}?all=true`,
            { method: "GET", credentials: "include" }
          );
          if (resp && resp.ok) {
            const raw = await resp.json();
            allSubmissions = (Array.isArray(raw) ? raw : (raw.answerPapers || [])).map((item) => ({
              ...item,
              studentName: `${item.firstName || ""} ${item.lastName || ""}`.trim(),
              status: item.status || "Pending",
            }));
          }
        } catch (err) {
          console.warn("Failed to fetch all submissions for export:", err);
        }
      }

      // Gather submitted papers from allSubmissions
      const graded = allSubmissions.filter(
        (item) =>
          item.status === "Graded" ||
          (item.totalScore != null && item.status === "Graded"),
      );
      const submittedNotGraded = allSubmissions.filter(
        (item) => item.status !== "Graded" && item.url,
      );

      setExportProgress({
        step: 2,
        message: "Checking pending submissions...",
      });
      await new Promise((r) => setTimeout(r, 200));

      // Fetch pending enrollments (no submission)
      let pendingEnrollments = [];
      try {
        const resp = await fetchWithAuth(
          `${serverUrl}/api/answer-paper/get/pending-submissions/test/${testId}`,
          { method: "GET", credentials: "include" },
        );
        if (resp && resp.ok) {
          pendingEnrollments = await resp.json();
        }
      } catch (err) {
        console.warn("Failed to fetch pending enrollments for CSV:", err);
      }

      setExportProgress({ step: 3, message: "Preparing export file..." });
      await new Promise((r) => setTimeout(r, 200));

      const rows = [];
      const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;

      // Graded students
      graded.forEach((s) => {
        const ref = s.referenceId || "";
        rows.push([
          esc(
            s.studentName || `${s.firstName || ""} ${s.lastName || ""}`.trim(),
          ),
          esc(ref),
          esc("Graded"),
          esc(
            typeof s.totalScore === "number"
              ? s.totalScore.toFixed(2)
              : (s.totalScore ?? ""),
          ),
        ]);
      });

      // Submitted but not graded
      submittedNotGraded.forEach((s) => {
        const ref = s.referenceId || "";
        rows.push([
          esc(
            s.studentName || `${s.firstName || ""} ${s.lastName || ""}`.trim(),
          ),
          esc(ref),
          esc("Submitted (Not Graded)"),
          esc("NOT Graded"),
        ]);
      });

      // Pending enrollments (no submission) -> score "AB"
      pendingEnrollments.forEach((p) => {
        const name =
          `${p.firstName || ""} ${p.lastName || ""}`.trim() ||
          p.studentName ||
          "";
        const ref = p.referenceId || "";
        rows.push([esc(name), esc(ref), esc("Pending Submission"), esc("AB")]);
      });

      if (rows.length === 0) {
        swalError("No students available to export.");
        setIsExportInProgress(false);
        setIsExportModalOpen(false);
        return;
      }

      setExportProgress({ step: 4, message: "Generating file..." });
      await new Promise((r) => setTimeout(r, 200));

      const header = [
        "Student Name",
        "Reference ID",
        "Status",
        "Total Score",
      ].map(esc);
      const csvContent = [
        header.join(","),
        ...rows.map((r) => r.join(",")),
      ].join("\r\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

      const testName = testInfo?.name || testInfo?.testName || "Assessment";
      const testType = testInfo?.testType || testInfo?.type || "";
      const sanitizedTestName = testName.replace(/[^a-zA-Z0-9]/g, "_");
      const sanitizedTestType = testType.replace(/[^a-zA-Z0-9]/g, "_");
      const fileName = testType
        ? `${sanitizedTestName} - ${sanitizedTestType}_Summary.csv`
        : `${sanitizedTestName}_Summary.csv`;

      saveAs(blob, fileName);

      setExportProgress({ step: 5, message: "Export complete!" });
      await new Promise((r) => setTimeout(r, 500));

      setIsExportModalOpen(false);
    } catch (err) {
      console.error("downloadSummarizedExport error", err);
      swalError("Failed to prepare summarized export.");
    } finally {
      setIsExportInProgress(false);
    }
  };

  // Detailed export - Excel with question-level scores
  const downloadDetailedExport = async () => {
    setIsExportInProgress(true);
    setExportProgress({ step: 1, message: "Gathering all student records..." });

    try {
      await new Promise((r) => setTimeout(r, 500));

      setExportProgress({
        step: 2,
        message: "Fetching detailed scores for all questions...",
      });

      // Rotating messages during the long API fetch
      const fetchMessages = [
        "Fetching detailed scores for all questions...",
        "Retrieving question-level data from server...",
        "Gathering marks for each student...",
        "Processing assessment results...",
      ];
      let messageIndex = 0;
      const messageInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % fetchMessages.length;
        setExportProgress({
          step: 2,
          message: fetchMessages[messageIndex],
        });
      }, 3000);

      // Fetch detailed scores from API
      const resp = await fetchWithAuth(
        `${serverUrl}/api/score/getAllScoresByQ/${testId}`,
        { method: "GET", credentials: "include" },
      );

      // Clear the rotating messages interval
      clearInterval(messageInterval);

      if (!resp || !resp.ok) {
        swalError("Failed to fetch detailed scores for export.");
        setIsExportInProgress(false);
        setIsExportModalOpen(false);
        return;
      }

      setExportProgress({
        step: 3,
        message: "Processing question-level data...",
      });
      await new Promise((r) => setTimeout(r, 300));

      const scoreData = await resp.json();
      const { distinctQuestionNumbers = [], allQuestions = [] } = scoreData;

      if (allQuestions.length === 0) {
        swalError("No graded data available to export.");
        setIsExportInProgress(false);
        setIsExportModalOpen(false);
        return;
      }

      setExportProgress({ step: 4, message: "Analyzing student scores..." });
      await new Promise((r) => setTimeout(r, 300));

      // Natural sort for question numbers (Q.1.1, Q.1.2, Q.2, etc.)
      const sortedQuestions = [...distinctQuestionNumbers].sort((a, b) => {
        const parseQ = (q) => {
          const parts = q.replace(/^Q\.?/i, "").split(".");
          return parts.map((p) => parseInt(p, 10) || 0);
        };
        const aParts = parseQ(a);
        const bParts = parseQ(b);
        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          const aVal = aParts[i] || 0;
          const bVal = bParts[i] || 0;
          if (aVal !== bVal) return aVal - bVal;
        }
        return 0;
      });

      // Build question info map: { questionNumber: { maxScore, courseOutcome, courseOutcomes (array) } }
      // Helper function to parse courseOutcome (can be comma-separated)
      const parseCOs = (coString) => {
        if (!coString || coString.trim() === "") return [];
        return coString
          .split(",")
          .map((co) => co.trim())
          .filter((co) => co !== "");
      };

      const questionInfoMap = {};
      allQuestions.forEach((q) => {
        if (!questionInfoMap[q.questionNumber]) {
          const coList = parseCOs(q.courseOutcome);
          questionInfoMap[q.questionNumber] = {
            maxScore: q.maxScore || 0,
            courseOutcome: q.courseOutcome || null, // Keep original for display
            courseOutcomes: coList, // Array of individual COs
          };
        }
      });

      // Get unique COs from data - split comma-separated values and get individual COs
      const allCOs = [
        ...new Set(
          allQuestions
            .filter((q) => q.courseOutcome && q.courseOutcome.trim() !== "")
            .flatMap((q) => parseCOs(q.courseOutcome)),
        ),
      ].sort((a, b) => {
        const aNum = parseInt(a.replace(/\D/g, ""), 10) || 0;
        const bNum = parseInt(b.replace(/\D/g, ""), 10) || 0;
        return aNum - bNum;
      });

      // Flag to check if we have CO data (only for Higher Education)
      const hasCOData =
        instituteInfo?.category === "HIGHER_EDUCATION" && allCOs.length > 0;

      // Group scores by student (using answerPaperId)
      const studentScoresMap = {};
      allQuestions.forEach((q) => {
        const studentKey = q.answerPaperId;
        if (!studentScoresMap[studentKey]) {
          studentScoresMap[studentKey] = {
            firstName: q.studentFirstName || "",
            lastName: q.studentLastName || "",
            scores: {},
          };
        }
        // Keep null/undefined for unattempted questions (don't default to 0)
        studentScoresMap[studentKey].scores[q.questionNumber] =
          q.obtainedScore !== undefined && q.obtainedScore !== null
            ? q.obtainedScore
            : null;
      });

      // Match with existing data to get referenceId and status
      const studentsArray = Object.entries(studentScoresMap).map(
        ([answerPaperId, studentData]) => {
          const matchedStudent = data.find(
            (d) => d._id === answerPaperId || d.answerPaperId === answerPaperId,
          );
          return {
            answerPaperId,
            name: `${studentData.firstName} ${studentData.lastName}`.trim(),
            referenceId: matchedStudent?.referenceId || "",
            status: matchedStudent?.status || "Graded",
            scores: studentData.scores,
          };
        },
      );

      // Sort students by referenceId or name
      studentsArray.sort((a, b) => {
        const aRef = parseInt(a.referenceId, 10) || 0;
        const bRef = parseInt(b.referenceId, 10) || 0;
        if (aRef !== bRef) return aRef - bRef;
        return a.name.localeCompare(b.name);
      });

      // Create Excel workbook
      const workbook = new ExcelJS.Workbook();
      const testName = testInfo?.name || testInfo?.testName || "Assessment";
      const worksheet = workbook.addWorksheet("Scores");

      // Calculate column count for header merge
      const fixedCols = 4; // Sr. No., Student Name, Reference ID, Status
      const questionCols = sortedQuestions.length;
      const coCols = hasCOData ? allCOs.length : 0;
      const totalColCount = fixedCols + questionCols + 1 + coCols * 2; // Questions + Total Marks + CO Scores + % Attainment

      // Row 1: Test name header (merged across all columns)
      const headerRow = worksheet.addRow([]);
      headerRow.getCell(fixedCols + Math.floor(questionCols / 2) + 1).value =
        testName;
      worksheet.mergeCells(1, 1, 1, totalColCount);
      headerRow.getCell(1).value = testName;
      headerRow.getCell(1).alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      headerRow.getCell(1).font = { bold: true, size: 14 };
      headerRow.height = 25;

      // Row 2: Column headers
      const headers = [
        "Sr. No.",
        "Student Name",
        "Reference ID",
        "Status",
        ...sortedQuestions.map((q) => {
          const info = questionInfoMap[q] || {
            maxScore: 1,
            courseOutcome: null,
          };
          const maxStr =
            info.maxScore === 1 ? "1 Mark" : `${info.maxScore} Marks`;
          // Only include CO info in header if courseOutcome exists AND we are showing CO data
          if (hasCOData && info.courseOutcome) {
            return `${q} (${maxStr} - ${info.courseOutcome})`;
          }
          return `${q} (${maxStr})`;
        }),
        "Total Marks",
        ...(hasCOData ? allCOs.map((co) => `${co} Score`) : []),
        ...(hasCOData ? allCOs.map((co) => `% ${co} Attainment`) : []),
      ];
      const headerDataRow = worksheet.addRow(headers);
      headerDataRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFEDD5" }, // Light orange
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      setExportProgress({ step: 5, message: "Calculating CO max scores..." });
      const coMaxScores = {};
      if (hasCOData) {
        allCOs.forEach((co) => {
          coMaxScores[co] = sortedQuestions
            .filter((q) => questionInfoMap[q]?.courseOutcomes?.includes(co))
            .reduce((sum, q) => {
              const qInfo = questionInfoMap[q];
              const coCount = qInfo?.courseOutcomes?.length || 1;
              return sum + (qInfo?.maxScore || 0) / coCount;
            }, 0);
        });
      }

      setExportProgress({ step: 6, message: "Building student score rows..." });
      await new Promise((r) => setTimeout(r, 200));

      // Data rows for each student
      studentsArray.forEach((student, idx) => {
        const rowData = [
          idx + 1,
          student.name,
          student.referenceId,
          student.status,
        ];

        // Question scores - show empty cell for unattempted questions
        sortedQuestions.forEach((q) => {
          const score = student.scores[q];
          if (score !== null && score !== undefined) {
            rowData.push(
              typeof score === "number" ? parseFloat(score.toFixed(2)) : score,
            );
          } else {
            rowData.push("");
          }
        });

        // Total score (Total Marks)
        const totalScore = Object.values(student.scores).reduce(
          (sum, s) => sum + (s ?? 0),
          0,
        );
        rowData.push(parseFloat(totalScore.toFixed(2)));

        // CO scores (only if we have CO data)
        if (hasCOData) {
          allCOs.forEach((co) => {
            const coScore = sortedQuestions
              .filter((q) => questionInfoMap[q]?.courseOutcomes?.includes(co))
              .reduce((sum, q) => {
                const qInfo = questionInfoMap[q];
                const coCount = qInfo?.courseOutcomes?.length || 1;
                const score = student.scores[q];
                if (score === null || score === undefined) return sum;
                return sum + score / coCount;
              }, 0);
            rowData.push(parseFloat(coScore.toFixed(2)));
          });

          // % CO Attainment columns
          allCOs.forEach((co) => {
            const coScore = sortedQuestions
              .filter((q) => questionInfoMap[q]?.courseOutcomes?.includes(co))
              .reduce((sum, q) => {
                const qInfo = questionInfoMap[q];
                const coCount = qInfo?.courseOutcomes?.length || 1;
                const score = student.scores[q];
                if (score === null || score === undefined) return sum;
                return sum + score / coCount;
              }, 0);

            const maxScore = coMaxScores[co] || 1;
            const attainmentPercent = (coScore / maxScore) * 100;
            rowData.push(parseFloat(attainmentPercent.toFixed(2)));
          });
        }

        const dataRow = worksheet.addRow(rowData);
        dataRow.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          if (colNumber > fixedCols) {
            cell.alignment = { horizontal: "center" };
          }
        });
      });

      // Set column widths
      worksheet.columns.forEach((col, i) => {
        if (i === 0)
          col.width = 8; // Sr. No.
        else if (i === 1)
          col.width = 20; // Student Name
        else if (i === 2)
          col.width = 12; // Reference ID
        else if (i === 3)
          col.width = 12; // Status
        else col.width = 15;
      });

      setExportProgress({ step: 6, message: "Generating Excel file..." });
      await new Promise((r) => setTimeout(r, 200));

      // Generate and download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const testType = testInfo?.testType || testInfo?.type || "";
      const sanitizedTestName = testName.replace(/[^a-zA-Z0-9]/g, "_");
      const sanitizedTestType = testType.replace(/[^a-zA-Z0-9]/g, "_");
      const fileName = testType
        ? `${sanitizedTestName} - ${sanitizedTestType}_Scores.xlsx`
        : `${sanitizedTestName}_Scores.xlsx`;
      saveAs(blob, fileName);

      setExportProgress({ step: 7, message: "Export complete!" });
      await new Promise((r) => setTimeout(r, 500));

      setIsExportModalOpen(false);
    } catch (err) {
      console.error("downloadDetailedExport error", err);
      swalError("Failed to prepare detailed Excel export.");
    } finally {
      setIsExportInProgress(false);
    }
  };

  const handleSpecialSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/test/update/${testId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ userPrompt: specialInstruction }),
        },
      );
      if (!response) return;
      // Update the userPrompt state with the new instruction
      setUserPrompt(specialInstruction);
      swalSuccess("Special grading instruction submitted successfully.");
      setIsSpecialModalOpen(false);
      setSpecialInstruction("");

      // Check if any papers are submitted / graded
      const hasPapersToRegrade = (totalRecords > 0) || (data && data.length > 0);
      if (hasPapersToRegrade) {
        const result = await swalConfirm(
          "Update Grading for Existing Papers?",
          "You've updated the grading instructions. Would you like to re-grade the papers that were already graded?\n\n⚠️ Warning: This will reset all previously graded papers and existing scores will be deleted.",
          "Yes, Re-grade All",
          "No, Keep Existing Grades",
        );
        if (result.isConfirmed) {
          const totalPaperCount = totalRecords || (data ? data.length : 0);
          // Show regrade progress modal
          setRegradeProgress({
            current: 0,
            total: totalPaperCount,
            message: "Resetting all papers for re-grading...",
          });
          setIsRegradeModalOpen(true);

          try {
            const regradeResponse = await fetchWithAuth(
              `${serverUrl}/api/answer-paper/regrade-all/${testId}`,
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                },
                credentials: "include",
              },
            );

            if (regradeResponse && regradeResponse.ok) {
              const regradeResult = await regradeResponse.json();
              const modifiedCount = regradeResult.modifiedCount ?? totalPaperCount;
              const matchedCount = regradeResult.matchedCount ?? totalPaperCount;

              setRegradeProgress({
                current: modifiedCount,
                total: matchedCount,
                message: "Refreshing data...",
              });

              // Update local state for current page
              setData((prevData) =>
                prevData.map((item) => ({ ...item, status: "Pending" })),
              );

              await updateAssessmentData(); // Refresh data from server

              setRegradeProgress({
                current: modifiedCount,
                total: matchedCount,
                message: "Complete!",
              });

              await new Promise((r) => setTimeout(r, 500));
              setIsRegradeModalOpen(false);

              swalSuccess(
                `Successfully reset ${modifiedCount} papers for re-grading.`,
              );
            } else {
              const errData = await regradeResponse?.json();
              setIsRegradeModalOpen(false);
              swalError(errData?.message || "Failed to reset papers for re-grading.");
            }
          } catch (regradeError) {
            setIsRegradeModalOpen(false);
            swalError(regradeError.message || "Failed to reset papers for re-grading.");
          }
        }
      }
    } catch (error) {
      swalError(error.error || "Failed to submit special grading instruction!");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Publish results: update test metadata then iterate graded students and notify each
  const publishResults = async () => {
    setIsPublishing(true);
    setPublishPhase("preparing");
    setPublishProgress("Updating test publish status...");
    try {
      const now = new Date();
      const timezoneOffset = now.getTimezoneOffset();
      const timezoneOffsetHours = -timezoneOffset / 60;

      const updateResp = await fetchWithAuth(
        `${serverUrl}/api/test/update/${testId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            scorePublishedDate: new Date().toISOString(),
            timezoneOffset: timezoneOffsetHours,
          }),
        },
      );

      if (!updateResp) {
        swalError("Failed to publish results (update failed).");
        setLoading(false);
        setIsPublishing(false);
        return;
      }

      setTestDetails((prev) => ({
        ...prev,
        scorePublishedDate: new Date().toISOString(),
      }));

      let allSubmissions = data;
      if (!isManualEntryTest) {
        try {
          const resp = await fetchWithAuth(
            `${serverUrl}/api/answer-paper/get-all/${testId}?all=true`,
            { method: "GET", credentials: "include" }
          );
          if (resp && resp.ok) {
            const raw = await resp.json();
            allSubmissions = (Array.isArray(raw) ? raw : (raw.answerPapers || [])).map((item) => ({
              ...item,
              studentName: `${item.firstName || ""} ${item.lastName || ""}`.trim(),
              status: item.status || "Pending",
            }));
          }
        } catch (err) {
          console.warn("Failed to fetch all submissions for publish:", err);
        }
      }

      const gradedStudents = allSubmissions.filter((d) => d.status === "Graded");
      if (gradedStudents.length === 0) {
        swalWarning("No graded submissions to publish.");
        setLoading(false);
        setIsPublishing(false);
        return;
      }

      let count = 0;
      for (const student of gradedStudents) {
        count++;
        const studentName = student.studentName || `${student.firstName || ""} ${student.lastName || ""}`.trim() || student._id;
        setCurrentStudentName(studentName);
        setPublishPhase("sending");
        setPublishProgress(`Publishing & notifying student ${count} of ${gradedStudents.length}...`);

        try {
          await fetchWithAuth(
            `${serverUrl}/api/answer-paper/notify-student/${student._id}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                notificationType: "all",
                includeAnswerPaper: publishWithAnswerPaper,
              }),
            },
          );
        } catch (innerErr) {
          console.error("Error notifying student", student._id, innerErr);
        }
      }

      setPublishPhase("completed");
      setPublishProgress("All results published successfully!");
      setCurrentStudentName("");

      swalSuccess("Results published and notifications queued.");
      setIsPublishModalOpen(false);
      await updateAssessmentData();
    } catch (error) {
      console.error("publishResults error", error);
      swalError("Failed to publish results!");
    } finally {
      setLoading(false);
      setIsPublishing(false);
      setPublishPhase("");
      setPublishProgress("");
      setCurrentStudentName("");
    }
  };

  // Handle Delete Submission
  const handleDeleteSubmission = async (answerPaperId) => {
    swalConfirm(
      "Are you sure you want to delete this submission?",
      "All scores associated with this submission will also be deleted!",
      "Yes, Continue!",
      "No, Cancel",
    ).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setActiveAction({ id: answerPaperId, type: "delete" });

          const response = await fetchWithAuth(
            `${serverUrl}/api/answer-paper/delete/${answerPaperId}`,
            {
              method: "DELETE",
              credentials: "include",
            },
          );
          if (!response) return;
          const responseInfo = await response.json();

          if (!response.ok) {
            swalError(responseInfo.message || "Failed to delete submission");
            return;
          }

          setData(
            data.filter((answerPaper) => answerPaper._id !== answerPaperId),
          );
          swalSuccess("Success!", "Submission deleted successfully");
          updateAssessmentData();
        } catch (err) {
          swalError(err.message || "Failed to delete submission");
        } finally {
          setActiveAction({ id: null, type: null });
        }
      }
    });
  };

  const handleNotifySingle = async (answerPaperId, notificationType) => {
    setActiveAction({ id: answerPaperId, type: notificationType });
    setLoading(true);
    try {
      const cached = data.find((d) => d._id === answerPaperId) || {};
      const studentName = cached.studentName || `${cached.firstName || ""} ${cached.lastName || ""}`.trim() || answerPaperId;
      const isAnnotationMode = instituteInfo?.feedbackMode === "ANNOTATION_ONLY" || instituteInfo?.feedbackMode === "ANNOTATION_AND_APP_FEEDBACK";
  

      let includeAnswerPaper = false;
      if (!isAnnotationMode) {
        const choice = await swalConfirm(
          "Include Answer Paper?",
          "Choose whether to include the original answer-paper PDF with the notification.",
          "With Answer Paper",
          "Without Answer Paper",
          true,
        );

        if (!choice) {
          setActiveAction({ id: null, type: null });
          setLoading(false);
          return;
        }

        if (choice.isConfirmed) {
          includeAnswerPaper = true;
        } else if (choice.dismiss === "cancel") {
          includeAnswerPaper = false;
        } else {
          setActiveAction({ id: null, type: null });
          setLoading(false);
          return;
        }
      }

      setPublishPhase("sending");
      setPublishProgress(`Notifying ${studentName} via ${notificationType}...`);

      const notifyResp = await fetchWithAuth(
        `${serverUrl}/api/answer-paper/notify-student/${answerPaperId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            notificationType: String(notificationType || "").toLowerCase(),
            includeAnswerPaper,
          }),
        },
      );

      if (notifyResp) {
        const notifyJson = await notifyResp.json().catch(() => ({}));
        if (notifyResp.ok) {
          swalSuccess(
            notifyJson.message ||
              `Notification (${notificationType}) sent to ${studentName}.`,
          );
        } else {
          swalError(
            notifyJson.message ||
              `Failed to notify ${studentName} via ${notificationType}.`,
          );
        }
      } else {
        swalError("Notification request failed.");
      }
    } catch (err) {
      console.error("handleNotifySingle error", err);
      swalError("Failed to notify student.");
    } finally {
      setActiveAction({ id: null, type: null });
      setLoading(false);
      setPublishPhase("");
      setPublishProgress("");
    }
  };

  const handleDownloadAllWithPrompt = async () => {
    const isAnnotationMode = instituteInfo?.feedbackMode === "ANNOTATION_ONLY" || instituteInfo?.feedbackMode === "ANNOTATION_AND_APP_FEEDBACK";
    try {
  

      if (isAnnotationMode) {
        setPublishWithAnswerPaper(false);
        await handleDownloadAllReports();
        return;
      }

      const choice = await swalConfirm(
        "Include Answer Paper?",
        "Choose whether to include the original answer-paper PDF with each student's report.",
        "With Answer Paper",
        "Without Answer Paper",
        true,
      );

      if (!choice) return;

      if (choice.isConfirmed) {
        setPublishWithAnswerPaper(true);
      } else if (choice.dismiss === "cancel") {
        setPublishWithAnswerPaper(false);
      } else {
        return;
      }

      await handleDownloadAllReports();
    } catch (err) {
      console.error("handleDownloadAllWithPrompt error", err);
      swalError("Failed to start download.");
    }
  };

  const allGraded = submissionCount > 0 && gradedCount === submissionCount;

  const gradingCompletionStatus = async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/answer-paper/get-all/${testId}`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        swalError(data.message || "Failed to fetch data");
      }
      response.data = data;
      const currentData = response.data.map((item) => ({
        ...item,
        studentName: `${item.firstName} ${item.lastName}`,
        status: item.status || "Pending",
      }));

      const hasRequestedSubmissions = currentData.some(
        (item) => item.status === "Requested",
      );

      if (!hasRequestedSubmissions) {
        setData(currentData);
        swalSuccess("Results refreshed successfully!");
      } else {
        swalWarning("Results are still being graded. Please wait.");
      }
    } catch (error) {
      swalError("Failed to check grading status");
    }
  };

  const pushGradesToCanvas = async (testId) => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/canvas/push-grades/test/${testId}`,
        {
          method: "post",
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to push grades to Canvas.",
        );
      }
      swalSuccess("Grades pushed to Canvas successfully!");
    } catch (err) {
      console.error(err);
      swalError(err.message);
    }
  };

  // Issue Certificates for all graded students
  const handleIssueCertificates = async () => {
    const confirmed = await swalConfirm(
      "Issue Certificates?",
      "Are you sure you want to generate and issue completion certificates to all graded students of this test?",
      "Yes, Issue Certificates",
      "Cancel",
    );

    if (!confirmed.isConfirmed) return;

    try {
      setIsGeneratingCerts(true);
      const result = await certificateService.issueBulkCertificates({
        testId: testId,
      });

      if (result && result.success) {
        swalSuccess(
          result.message ||
            "Certificates successfully issued for all graded students!",
        );
      } else {
        throw new Error(result?.message || "Failed to issue certificates.");
      }
    } catch (err) {
      console.error("Bulk certificate generation error:", err);
      swalError(
        err?.response?.data?.message ||
          err.message ||
          "An error occurred while issuing certificates.",
      );
    } finally {
      setIsGeneratingCerts(false);
    }
  };

  return (
    <motion.div
      className="submitted-assessment"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      {loading && (
        <Loader style={{ position: "fixed", top: "8rem", zIndex: "9999" }} />
      )}
      <motion.div className={`upper ${isManualEntryTest ? "full-width" : ""}`}>
        <motion.div
          className="stats-container"
          initial={{ scale: 0.5 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div className="stats-box">
            {[
              { title: "Submission Rate", value: submissionRate },
              { title: "Average Score", value: averageScore },
              { title: "Standard Deviation", value: `±${standardDeviation}%` },
            ].map((stat, index) => (
              <motion.div
                key={index}
                className="stat-card"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <h3>{stat.title}</h3>
                <p>{stat.value}</p>
                <div className="card-blur"></div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div className="stats-box">
            {[
              { title: "Graded", value: `${gradedCount}/${submissionCount}` },
              {
                title: "Export Scores",
                onClick: openExportModal,
                disabled: submissionCount <= 0,
                icon: "/imgs/download_icon.svg",
              },
              {
                title: "To Be Graded",
                value: pendingCount,
                onClick: handleSortPending,
              },
            ].map((stat, index) => (
              <motion.button
                key={index}
                type="button"
                className="stat-card"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.95 }}
                onClick={stat.onClick}
                disabled={stat.disabled}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <h3>{stat.title}</h3>
                {stat.showLoader ? (
                  <Loader style={{ position: "relative", left: "0" }} />
                ) : (
                  <p>{stat.value}</p>
                )}
                {stat.icon && (
                  <img
                    src={stat.icon}
                    alt="icon"
                    className="stat-img"
                    width={30}
                  />
                )}
                <div className="card-blur"></div>
              </motion.button>
            ))}
          </motion.div>
        </motion.div>
        {!isManualEntryTest && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="card-actions">
              <Card
                title="Special Grading Instruction"
                description={userPrompt || "No instructions available."}
              >
                <button
                  className="btn-edit"
                  onClick={() => setIsSpecialModalOpen(true)}
                  disabled={
                    testDetails.scorePublishedDate != null ||
                    isOnlineExamination
                  }
                  title={
                    isOnlineExamination
                      ? "Cannot edit special grading instructions for online exams"
                      : testDetails.scorePublishedDate != null
                      ? "Cannot edit instructions after results are published"
                      : "Edit special grading instruction"
                  }
                >
                  <img src="/imgs/pencil_icon.svg" alt="edit-btn" width={25} />
                </button>
              </Card>
            </div>
          </motion.div>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <div className="action-buttons">
          {testDetails.externalId && user?.sourceSystem === "Canvas" && (
            <motion.button
              className="grade-pending-btn"
              onClick={() => pushGradesToCanvas(testDetails._id)}
              disabled={
                data.filter(
                  (item) =>
                    item?.status === "Graded" || item?.status === "Review",
                ).length === 0
              }
            >
              <i className="fa-solid fa-upload"></i> Push Grades to Canvas
            </motion.button>
          )}

          {testDetails.scorePublishedDate == null &&
            allGraded &&
            data.length > 0 && !isManualEntryTest && (
              <div className="publish-btn-tooltip-wrapper">
                <motion.button
                  className="grade-pending-btn"
                  onClick={() => {
                  setPublishConfirmInput("");
                  setPublishStep(1);
                  setIsPublishModalOpen(true);
                }}
                  disabled={!user?.isPublishResultAllowed}
                >
                  <i className="fa-solid fa-upload"></i> Publish Results
                </motion.button>
                {!user?.isPublishResultAllowed && (
                  <div className="publish-btn-tooltip-card">
                    <i className="fa-solid fa-lock" style={{ marginRight: "6px", fontSize: "0.8rem" }}></i>
                    You do not have permission to publish results. Kindly contact the administrator to request access.
                  </div>
                )}
              </div>
            )}

          {/* Notify All button - visible after publish, opens modal with options */}
          {testDetails.scorePublishedDate != null && data.length > 0 && (
            <motion.button
              className="download-all-btn"
              onClick={() => setIsNotifyAllModalOpen(true)}
              disabled={loading || isPublishing}
              title={
                loading || isPublishing
                  ? "Processing..."
                  : "Notify All Students"
              }
            >
              <i className="fa-solid fa-bell" />
              &nbsp; Notify All
            </motion.button>
          )}

        {!isManualEntryTest && !isHomework && (
          <motion.button
            className="grade-pending-btn"
            onClick={handleInitiateSummarize}
            disabled={!isSummaryButtonActive}
          >
            AI Performance Generator{" "}
            <span className="pending-count">{summarizePendingCount}</span>
          </motion.button>
        )}
          {!isManualEntryTest && (
            <motion.button
              onClick={handleGradePending}
              className="grade-pending-btn"
              whileTap={{ scale: 0.95 }}
              disabled={pendingCount === 0}
            >
              Start Grading{" "}
              <span className="pending-count">{pendingCount}</span>
            </motion.button>
          )}
          {isManualEntryTest && (
            <motion.button
              onClick={handleGradePending}
              className="grade-pending-btn"
              whileTap={{ scale: 0.95 }}
              disabled={pendingCount === 0}
            >
              Enter Score{" "}
              <span className="pending-count">{pendingCount}</span>
            </motion.button>
          )}
          {/* Score Modifier Button */}
          <motion.button
            onClick={handleOpenScoreModifier}
            className="action-btns"
            whileTap={{ scale: 0.95 }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
            title="Adjust or moderate scores with grace marks or strict standard"
          >
            <i className="fa-solid fa-sliders" /> Adjust All Scores
            {false && (
              <span className="pending-count">
                {selectedRowIds.size}
              </span>
            )}
          </motion.button>

          {data.some((item) => item.status === "Requested") && (
            <motion.button
              onClick={async () => {
                await gradingCompletionStatus();
              }}
              className="action-btns"
              whileTap={{ scale: 0.95 }}
            >
              <i className="fa-solid fa-rotate-right"></i> Refresh Results
            </motion.button>
          )}
          {!isManualEntryTest && !isHomework && instituteInfo?.category === "K12_SCHOOL" && (
          <motion.button
            onClick={() => navigate(`/instructor/score-by-question/${testId}`)}
            className="action-btns"
            whileTap={{ scale: 0.95 }}
            disabled={data.length < 1}
          >
            View By Question
          </motion.button>
        )}
          {testDetails.certificateTemplateId && (
            <motion.button
              onClick={handleIssueCertificates}
              className="action-btns"
              whileTap={{ scale: 0.95 }}
              disabled={loading || isPublishing || isGeneratingCerts || data.length < 1}
              style={{
                backgroundColor: "var(--color-2, #3b82f6)",
                color: "#ffffff",
                borderColor: "var(--color-2, #3b82f6)",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
              title="Issue verified completion certificates to all graded students"
            >
              {isGeneratingCerts ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" /> Issuing Certificates...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-award" /> Issue Certificates
                </>
              )}
            </motion.button>
          )}

          {/* Smart Split Document Viewer Button with Dynamic Status */}
          {shouldShowSmartSplit && (
            <motion.button
              onClick={handleOpenSmartSplitViewer}
              className="action-btns"
              whileTap={{ scale: 0.95 }}
              disabled={loading || isPublishing || smartSplitLoading}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
              title={
                smartSplitStatus?.status === 'queued'
                  ? 'Preparing to split answer sheets...'
                  : smartSplitStatus?.status === 'processing'
                  ? `Splitting papers: ${smartSplitStatus.processedCount} of ${smartSplitStatus.totalPages || '?'} pages completed`
                  : (smartSplitStatus?.status === 'completed' || smartSplitStatus?.status === 'done')
                  ? `Split complete (${smartSplitStatus.papersCount} papers). Click to review.`
                  : smartSplitStatus?.status === 'failed'
                  ? 'Processing could not complete. Click to view details.'
                  : 'Review split student answer booklets'
              }
            >
              {smartSplitLoading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" /> Loading Papers...
                </>
              ) : smartSplitStatus?.status === 'queued' ? (
                <>
                  <i className="fa-solid fa-clock fa-spin" style={{ color: "#f59e0b" }} /> Preparing Papers...
                </>
              ) : smartSplitStatus?.status === 'processing' ? (
                <>
                  <i className="fa-solid fa-gear fa-spin" style={{ color: "#3b82f6" }} /> Splitting... ({smartSplitStatus.processedCount}/{smartSplitStatus.totalPages || '?'})
                </>
              ) : (smartSplitStatus?.status === 'completed' || smartSplitStatus?.status === 'done') ? (
                <>
                  <i className="fa-solid fa-file-circle-check" style={{ color: "#10b981" }} /> Split Papers
                  {smartSplitStatus.papersCount > 0 && (
                    <span
                      style={{
                        background: "#10b981",
                        color: "#ffffff",
                        borderRadius: "10px",
                        padding: "1px 6px",
                        fontSize: "11px",
                        fontWeight: 700,
                        marginLeft: "2px",
                      }}
                    >
                      {smartSplitStatus.papersCount}
                    </span>
                  )}
                </>
              ) : smartSplitStatus?.status === 'failed' ? (
                <>
                  <i className="fa-solid fa-triangle-exclamation" style={{ color: "#ef4444" }} /> Smart Split (Failed)
                </>
              ) : (
                <>
                  <i className="fa-solid fa-file-pdf" style={{ color: "#ef4444" }} /> Smart Split Papers
                </>
              )}
            </motion.button>
          )}

          {/* Smart Bulk Download Reports Button - Available whenever there are graded submissions */}
          {data.length > 0 && (gradedCount > 0 || data.some((d) => d.status === "Graded" || d.status === "Review")) && (
            <SmartDownloadButton
              isBulk={true}
              testId={testId}
              feedbackMode={instituteInfo?.feedbackMode || "APP_FEEDBACK_ONLY"}
              hasSummary={data.some((d) => d.summary || d.summaryStatus === "Summarized")}
              testName={testDetails?.name || testInfo?.name || "Assessment"}
              disabled={loading || isPublishing}
              serverUrl={serverUrl}
              fetchWithAuth={fetchWithAuth}
              swalError={swalError}
              swalSuccess={swalSuccess}
              buttonText="Download All Reports"
              iconOnly={false}
              className="action-btns"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                whiteSpace: "nowrap",
                backgroundColor: "var(--color-2, #3b82f6)",
                color: "#ffffff",
                padding: "8px 14px",
                borderRadius: "100px",
                border: "1px solid var(--color-2, #3b82f6)",
                fontWeight: 600,
                fontSize: "13px",
              }}
              title="Download consolidated reports for all graded students"
              onOpenBulkReorderModal={() => setIsBulkAnnotationModalOpen(true)}
            />
          )}
        </div>
        <Table
          data={data}
          columns={columns}
          caption="Submissions"
          textAlign={"center"}
          serverSide={!isManualEntryTest}
          totalRecords={isManualEntryTest ? data.length : totalRecords}
          page={page}
          pageSize={pageSize}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
          }}
          sortBy={sortBy}
          sortOrder={sortOrder === "asc" ? "ascending" : "descending"}
          onPageChange={(newPage) => setPage(newPage)}
          onSortChange={handleSortChange}
          onSearchChange={(searchVal) => {
            setSearchTerm(searchVal);
            setPage(1);
          }}
          isCheckBoxAvailable={false}
          selectedRowIds={selectedRowIds}
          onRowSelect={handleRowSelect}
          onSelectAll={handleSelectAll}
          disableRowClickSelection={true}
        />
      </motion.div>

      {/* Score Adjustment & Moderation Modal */}
      <ScoreAdjustmentModal
        isOpen={scoreAdjustModal.isOpen}
        onClose={() => setScoreAdjustModal({ isOpen: false, student: null })}
        testId={testId}
        isManualEntryTest={isManualEntryTest}
        serverGradedCount={serverGradedCount}
        data={data}
        student={scoreAdjustModal.student}
        onApplied={updateAssessmentData}
      />

      {/* Single-Student Score Adjustment Modal */}
      <div style={{ display: "none" }}
        data-closed={true}
        data-onclose={null}
        data-student={null}
        onApplied={updateAssessmentData}
      />
      <Modal
        isOpen={false /* deprecated old modal */}
        onClose={() => {
          setIsScoreModifierModalOpen(false);
          setScoreModifierResults(null);
          setSelectedModifierRowIds(new Set());
        }}
        maxWidth="680px"
      >
        <div className="score-modifier-modal" style={{ padding: "0.5rem" }}>
          <h2 style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
            <i className="fa-solid fa-sliders" style={{ color: "#f09a21" }}></i> Score Modifier
          </h2>
          <p style={{ color: "#6b7280", fontSize: "0.875rem", marginBottom: "1.25rem" }}>
            Select grading policy and threshold percentage to preview modified scores for selected students.
          </p>

          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: "0.75rem",
              marginBottom: "1.25rem",
              flexWrap: "wrap",
            }}
          >
            <div className="form-group" style={{ flex: 1, minWidth: "160px", marginBottom: 0 }}>
              <label style={{ display: "block", marginBottom: "0.35rem", fontWeight: "600", fontSize: "0.875rem", color: "#374151" }}>
                Grading Policy:
              </label>
              <select
                value={gradingPolicy}
                onChange={(e) => setGradingPolicy(e.target.value)}
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 0.75rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  background: "#fff",
                  boxSizing: "border-box",
                }}
              >
                <option value="Lenient">Lenient</option>
                <option value="Strict">Strict</option>
              </select>
            </div>

            <div className="form-group" style={{ flex: 1, minWidth: "140px", marginBottom: 0 }}>
              <label style={{ display: "block", marginBottom: "0.35rem", fontWeight: "600", fontSize: "0.875rem", color: "#374151" }}>
                Threshold Percentage:
              </label>
              <select
                value={thresholdPercentage}
                onChange={(e) => setThresholdPercentage(Number(e.target.value))}
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 0.75rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  background: "#fff",
                  boxSizing: "border-box",
                }}
              >
                <option value={0.25}>25%</option>
                <option value={0.5}>50%</option>
              </select>
            </div>

            <button
              type="button"
              onClick={handleCheckScore}
              disabled={isCheckingScore}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.4rem",
                height: "38px",
                padding: "0 0.85rem",
                borderRadius: "8px",
                background: "#f09a21",
                color: "#fff",
                border: "none",
                fontWeight: "600",
                fontSize: "0.8125rem",
                cursor: isCheckingScore ? "not-allowed" : "pointer",
                opacity: isCheckingScore ? 0.7 : 1,
                whiteSpace: "nowrap",
                flexShrink: 0,
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                transition: "opacity 0.2s, background-color 0.2s",
              }}
            >
              {isCheckingScore ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin"></i> Checking...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-calculator"></i> Check Score
                </>
              )}
            </button>
          </div>

          {scoreModifierResults && (
            <div
              style={{
                marginTop: "1rem",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                overflow: "hidden",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
            >
              <div
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    textAlign: "left",
                    fontSize: "0.875rem",
                  }}
                >
                  <thead>
                    <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                      <th style={{ padding: "10px 14px", width: "44px", textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={
                            scoreModifierResults.length > 0 &&
                            scoreModifierResults.every((item) =>
                              selectedModifierRowIds.has(item.scoreId || item.answerPaperId)
                            )
                          }
                          onChange={handleSelectAllModifierRows}
                          title="Select all students"
                          style={{
                            cursor: "pointer",
                            width: "16px",
                            height: "16px",
                            accentColor: "#f09a21",
                          }}
                        />
                      </th>
                      <th style={{ padding: "10px 14px", fontWeight: "600", color: "#374151" }}>
                        Student Name
                      </th>
                      <th style={{ padding: "10px 14px", fontWeight: "600", color: "#374151", textAlign: "center" }}>
                        Current Score
                      </th>
                      <th style={{ padding: "10px 14px", fontWeight: "600", color: "#374151", textAlign: "center" }}>
                        New Score
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {scoreModifierResults.length === 0 ? (
                      <tr>
                        <td colSpan="4" style={{ padding: "16px", textAlign: "center", color: "#6b7280" }}>
                          No score details found for the selected students.
                        </td>
                      </tr>
                    ) : (
                      scoreModifierResults.map((item, idx) => {
                        const sName = item.studentName || item["student name"] || "Unknown";
                        const cScore = item.currentScore ?? item["current score"] ?? 0;
                        const nScore = item.newScore ?? item["new score"] ?? 0;
                        const diff = Math.round((nScore - cScore) * 100) / 100;
                        const rowKey = item.scoreId || item.answerPaperId;
                        const isSelected = selectedModifierRowIds.has(rowKey);
                        return (
                          <tr
                            key={rowKey || idx}
                            onClick={() => handleToggleModifierRow(rowKey)}
                            style={{
                              borderBottom: idx !== scoreModifierResults.length - 1 ? "1px solid #f3f4f6" : "none",
                              background: isSelected ? "#fffbeb" : idx % 2 === 0 ? "#ffffff" : "#fbfbfb",
                              cursor: "pointer",
                              transition: "background-color 0.15s ease",
                            }}
                          >
                            <td
                              style={{ padding: "10px 14px", textAlign: "center", width: "44px" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleModifierRow(rowKey)}
                                style={{
                                  cursor: "pointer",
                                  width: "16px",
                                  height: "16px",
                                  accentColor: "#f09a21",
                                }}
                              />
                            </td>
                            <td style={{ padding: "10px 14px", fontWeight: "500", color: "#111827" }}>
                              {sName}
                            </td>
                            <td style={{ padding: "10px 14px", textAlign: "center", color: "#4b5563" }}>
                              {cScore}
                            </td>
                            <td style={{ padding: "10px 14px", textAlign: "center", fontWeight: "600", color: diff < 0 ? "#dc2626" : "#059669" }}>
                              {nScore}
                              {diff > 0 && (
                                <span
                                  style={{
                                    marginLeft: "8px",
                                    fontSize: "0.75rem",
                                    color: "#10b981",
                                    background: "#ecfdf5",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    fontWeight: "600",
                                  }}
                                >
                                  +{diff}
                                </span>
                              )}
                              {diff < 0 && (
                                <span
                                  style={{
                                    marginLeft: "8px",
                                    fontSize: "0.75rem",
                                    color: "#ef4444",
                                    background: "#fef2f2",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    fontWeight: "600",
                                  }}
                                >
                                  {diff}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Bottom Actions: Apply Changes */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "0.75rem 1rem",
                  background: "#f9fafb",
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <span style={{ fontSize: "0.85rem", color: "#4b5563", fontWeight: "500" }}>
                  {selectedModifierRowIds.size} of {scoreModifierResults.length} student{scoreModifierResults.length === 1 ? "" : "s"} selected
                </span>
                <div style={{ display: "flex", gap: "0.75rem" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setIsScoreModifierModalOpen(false);
                      setScoreModifierResults(null);
                      setSelectedModifierRowIds(new Set());
                    }}
                    disabled={isApplyingPolicy}
                    style={{
                      padding: "0.5rem 1rem",
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      background: "#ffffff",
                      color: "#374151",
                      fontWeight: "500",
                      fontSize: "0.875rem",
                      cursor: isApplyingPolicy ? "not-allowed" : "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleApplyGradingPolicy}
                    disabled={selectedModifierRowIds.size === 0 || isApplyingPolicy}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.5rem 1.15rem",
                      borderRadius: "6px",
                      background: selectedModifierRowIds.size === 0 || isApplyingPolicy ? "#d1d5db" : "#f09a21",
                      color: "#fff",
                      border: "none",
                      fontWeight: "600",
                      fontSize: "0.875rem",
                      cursor: selectedModifierRowIds.size === 0 || isApplyingPolicy ? "not-allowed" : "pointer",
                      boxShadow: selectedModifierRowIds.size > 0 && !isApplyingPolicy ? "0 2px 4px rgba(240, 154, 33, 0.25)" : "none",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {isApplyingPolicy ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin"></i> Applying Changes...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-check"></i> Apply Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={isSpecialModalOpen}
        onClose={() => setIsSpecialModalOpen(false)}
      >
        <h2>Special Grading Instruction</h2>
        <form
          onSubmit={handleSpecialSubmit}
          className="special-instruction-form"
        >
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Instruction:
            </label>
            <div className="textarea-container">
              <textarea
                type="textarea"
                name="specialInstruction"
                value={specialInstruction}
                onChange={(e) => setSpecialInstruction(e.target.value)}
                required
                placeholder="Enter your special grading instructions here..."
                style={{ paddingRight: aiHasActions ? "120px" : "60px" }}
                disabled={aiHasActions}
                title={
                  aiHasActions ? "Accept or undo the AI suggestion to edit" : ""
                }
              />
              <div className="ai-controls">
                <button
                  type="button"
                  className="ai-btn"
                  onClick={handleGenerateUserPromptAI}
                  disabled={aiLoading}
                  title={
                    specialInstruction.trim()
                      ? "Rewrite with AI"
                      : "Write with AI"
                  }
                >
                  {aiLoading ? (
                    <i className="fa-solid fa-spinner fa-spin"></i>
                  ) : (
                    <i className="fa-solid fa-robot"></i>
                  )}
                </button>

                {aiHasActions && (
                  <div className="ai-actions">
                    <button
                      type="button"
                      className="ai-action-btn accept"
                      onClick={handleAiAccept}
                      title="Accept AI suggestion"
                    >
                      <i className="fa-solid fa-check"></i>
                    </button>
                    <button
                      type="button"
                      className="ai-action-btn undo"
                      onClick={handleAiUndo}
                      title="Undo AI changes"
                    >
                      <i className="fa-solid fa-undo"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <button
            type="submit"
            className="btn-submit"
            disabled={
              isSubmitting || specialInstruction === userPrompt || aiHasActions
            }
          >
            {isSubmitting ? (
              <Loader
                style={{ fontSize: "1.5rem", left: "0", color: "#fff" }}
              />
            ) : (
              <>
                <i className="fa-solid fa-floppy-disk"></i> Save Instruction
              </>
            )}
          </button>
        </form>
      </Modal>
      <Modal
        isOpen={isSummaryModalOpen}
        onClose={() => setIsSummaryModalOpen(false)}
        maxWidth={"1200px"}
      >
        {isSummaryGenerating ? (
          <div className="loading-container">
            <p style={{ marginLeft: "48%", marginBottom: "10px" }}>
              Generating ...
            </p>
            <Loader style={{ fontSize: "50px" }} />
          </div>
        ) : (
          <>
            {currentAnswerPaper?.summary ? (
              <div className="summary-content">
                <div ref={summaryRef}>
                  <PerformanceReport
                    summary={{
                      studentName: `${currentAnswerPaper.firstName} ${currentAnswerPaper.lastName}`,
                      testName: testInfo.name,
                      classId: classId,
                      instructorName: classInfo.instructorName,
                      overallPerformance:
                        currentAnswerPaper?.summary?.overallPerformance || "",
                      strengths: currentAnswerPaper?.summary?.strengths || [],
                      weaknesses: currentAnswerPaper?.summary?.weaknesses || [],
                      recommendations:
                        currentAnswerPaper?.summary?.recommendations || [],
                      createdAt: currentAnswerPaper?.summary?.createdAt || "",
                      updatedAt: currentAnswerPaper?.summary?.updatedAt || "",
                    }}
                    isSummaryEditing={false}
                    onSaveSummary={false}
                    handleSaveSummary={() => {}}
                  />
                </div>
                <div className="modal-actions">
                  <button
                    className="btn-submit"
                    onClick={handlePrintSummary}
                    title="Print the performance report"
                  >
                    <i className="fa-solid fa-print"></i> Print
                  </button>
                </div>
              </div>
            ) : (
              <p>No summary available. Retry Generating.</p>
            )}
          </>
        )}
      </Modal>
      <Modal
        isOpen={isPublishModalOpen}
        onClose={() => {
          if (!isPublishing) {
            setIsPublishModalOpen(false);
            setPublishStep(1);
            setPublishConfirmInput("");
            setPublishMethod(null);
          }
        }}
        maxWidth="640px"
      >
        <div className="publish-danger-modal">
          {!isPublishing ? (
            <>
              {/* ── Step 1: Choose Method ── */}
              {publishStep === 1 && (
                <div className="publish-step-content">
                  {/* Action buttons – choose how to publish */}
                  <div
                    className="publish-action-section"
                    style={{ marginTop: "1rem" }}
                  >
                    <h2
                      className="publish-action-section-label"
                    >
                      Choose Publish Method:
                    </h2>
                    <div className="publish-action-grid">
                      <button
                        onClick={() => {
                          setPublishMethod("just-publish");
                          setPublishStep(2);
                        }}
                        className="publish-final-btn publish-final-btn--primary"
                        title="Publish results for students to view in GradeMe AI App"
                      >
                        <div className="publish-final-btn-icon">
                          <i className="fa-solid fa-eye"></i>
                        </div>
                        <div className="publish-final-btn-text">
                          <strong>Publish to GradeMe AI</strong>
                          <span>
                            Students will be able to view their results, by
                            logging into the GradeMeAI.
                          </span>
                        </div>
                      </button>

                      <button
                        onClick={() => {
                          setPublishMethod("notify-without-pdf");
                          setPublishStep(2);
                        }}
                        className="publish-final-btn publish-final-btn--notify"
                        title="Publish and notify students via email (without answer paper)"
                      >
                        <div className="publish-final-btn-icon">
                          <i className="fa-solid fa-bell"></i>
                        </div>
                        <div className="publish-final-btn-text">
                          <strong>Send Score Reports</strong>
                          <span>
                            Students will receive their detailed Score Report
                            directly via Email and WhatsApp.
                          </span>
                        </div>
                      </button>

                      <button
                        onClick={() => {
                          setPublishMethod("notify-with-pdf");
                          setPublishStep(2);
                        }}
                        className="publish-final-btn publish-final-btn--full"
                        title="Publish and notify students with original answer paper attached"
                      >
                        <div className="publish-final-btn-icon">
                          <i className="fa-solid fa-file-pdf"></i>
                        </div>
                        <div className="publish-final-btn-text">
                          <strong>Send Score Reports with Answer Paper</strong>
                          <span>
                            Students will receive their detailed Score Report
                            along with their original Answer Paper directly via
                            Email and WhatsApp.
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step 2: Danger & Confirm ── */}
              {publishStep === 2 && (
                <div className="publish-step-content">
                  {/* Danger Icon */}
                  <div className="publish-danger-icon-wrapper">
                    <div className="publish-danger-icon-pulse"></div>
                    <div className="publish-danger-icon">
                      <i className="fa-solid fa-triangle-exclamation"></i>
                    </div>
                  </div>

                  <h2 className="publish-danger-title">Confirm Publish</h2>
                  <p className="publish-danger-subtitle">
                    This is a critical and irreversible action
                  </p>

                  {/* Impact Warning Box */}
                  <div className="publish-danger-warning-box">
                    <div className="publish-danger-warning-header">
                      <i className="fa-solid fa-shield-halved"></i>
                      <span>What happens when you publish?</span>
                    </div>
                    <ul className="publish-danger-impact-list">
                      <li>
                        <i className="fa-solid fa-users"></i>
                        <span>
                          Students will immediately be able to see their results
                        </span>
                      </li>
                      <li>
                        <i className="fa-solid fa-lock"></i>
                        <span>
                          This action <strong>cannot be undone</strong> —
                          results will be permanently visible to students
                        </span>
                      </li>
                      <li>
                        <i className="fa-solid fa-clock-rotate-left"></i>
                        <span>
                          Make sure all grades have been reviewed and finalized
                          before publishing
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div
                    className="publish-confirm-instruction"
                    style={{ marginTop: "1.5rem" }}
                  >
                    <p>
                      To confirm, type{" "}
                      <strong className="publish-confirm-phrase">
                        publish results
                      </strong>{" "}
                      in the box below:
                    </p>
                  </div>

                  <div className="publish-confirm-input-wrapper">
                    <input
                      type="text"
                      className={`publish-confirm-input ${publishConfirmInput.toLowerCase().trim() === "publish results" ? "publish-confirm-input--valid" : ""}`}
                      value={publishConfirmInput}
                      onChange={(e) => setPublishConfirmInput(e.target.value)}
                      onPaste={(e) => e.preventDefault()}
                      onDrop={(e) => e.preventDefault()}
                      placeholder="Type here..."
                      autoFocus
                      spellCheck={false}
                      autoComplete="off"
                    />
                    {publishConfirmInput.toLowerCase().trim() ===
                      "publish results" && (
                      <div className="publish-confirm-check">
                        <i className="fa-solid fa-circle-check"></i>
                      </div>
                    )}
                  </div>

                  <div
                    className="publish-step-actions"
                    style={{
                      justifyContent: "space-between",
                      marginTop: "1.5rem",
                    }}
                  >
                    <button
                      className="publish-back-btn"
                      onClick={() => {
                        setPublishStep(1);
                        setPublishConfirmInput("");
                        setPublishMethod(null);
                      }}
                    >
                      <i
                        className="fa-solid fa-arrow-left"
                        style={{ marginRight: "6px" }}
                      ></i>
                      Go back
                    </button>
                    <button
                      className="publish-proceed-btn"
                      disabled={
                        publishConfirmInput.toLowerCase().trim() !==
                        "publish results"
                      }
                      style={{
                        opacity:
                          publishConfirmInput.toLowerCase().trim() !==
                          "publish results"
                            ? 0.5
                            : 1,
                        cursor:
                          publishConfirmInput.toLowerCase().trim() !==
                          "publish results"
                            ? "not-allowed"
                            : "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onClick={() => {
                        if (publishMethod === "just-publish") {
                          handleJustPublish();
                        } else if (publishMethod === "notify-without-pdf") {
                          handleNotifyStudents(false);
                        } else if (publishMethod === "notify-with-pdf") {
                          handleNotifyStudents(true);
                        }
                      }}
                    >
                      I understand, Publish!
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* ── Publishing in progress ── */
            <div
              className="publish-progress-panel"
              style={{
                background:
                  publishPhase === "preparing"
                    ? "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)"
                    : publishPhase === "generating"
                      ? "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)"
                      : publishPhase === "downloading"
                        ? "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)"
                        : publishPhase === "sending"
                          ? "linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)"
                          : publishPhase === "completed"
                            ? "linear-gradient(135deg, #e8f5e8 0%, #a5d6a7 100%)"
                            : "#f8f9fa",
                borderColor:
                  publishPhase === "preparing"
                    ? "#2196f3"
                    : publishPhase === "generating"
                      ? "#ff9800"
                      : publishPhase === "downloading"
                        ? "#9c27b0"
                        : publishPhase === "sending"
                          ? "#4caf50"
                          : publishPhase === "completed"
                            ? "#2e7d32"
                            : "#e0e0e0",
              }}
            >
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {publishPhase !== "completed" && (
                  <Loader
                    fontSize="50px"
                    style={{
                      left: "0",
                      color:
                        publishPhase === "preparing"
                          ? "#1976d2"
                          : publishPhase === "generating"
                            ? "#f57c00"
                            : publishPhase === "downloading"
                              ? "#7b1fa2"
                              : publishPhase === "sending"
                                ? "#388e3c"
                                : "#666",
                    }}
                  />
                )}
                {publishPhase === "completed" && (
                  <div
                    style={{
                      fontSize: "50px",
                      color: "#2e7d32",
                      animation: "bounce 1s ease-in-out",
                    }}
                  >
                    <i className="fa-solid fa-check-circle"></i>
                  </div>
                )}
              </div>

              <div
                style={{
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                  color:
                    publishPhase === "preparing"
                      ? "#1565c0"
                      : publishPhase === "generating"
                        ? "#f57c00"
                        : publishPhase === "downloading"
                          ? "#7b1fa2"
                          : publishPhase === "sending"
                            ? "#2e7d32"
                            : publishPhase === "completed"
                              ? "#1b5e20"
                              : "#1e40af",
                  textAlign: "center",
                  animation:
                    publishPhase !== "completed" ? "pulse 2s infinite" : "none",
                  textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                {publishProgress}
              </div>

              {currentStudentName && publishPhase !== "completed" && (
                <div
                  style={{
                    fontSize: "1rem",
                    color: "#6b7280",
                    textAlign: "center",
                    fontWeight: "500",
                    padding: "0.5rem 1rem",
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    borderRadius: "8px",
                    border: "1px solid rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <i
                    className="fa-solid fa-user"
                    style={{ marginRight: "0.5rem" }}
                  ></i>
                  {currentStudentName}
                </div>
              )}

              <div
                style={{
                  fontSize: "0.9rem",
                  color:
                    publishPhase === "preparing"
                      ? "#1976d2"
                      : publishPhase === "generating"
                        ? "#f57c00"
                        : publishPhase === "downloading"
                          ? "#7b1fa2"
                          : publishPhase === "sending"
                            ? "#388e3c"
                            : publishPhase === "completed"
                              ? "#2e7d32"
                              : "#6b7280",
                  textAlign: "center",
                  fontWeight: "500",
                  maxWidth: "400px",
                }}
              >
                {publishPhase === "preparing" &&
                  "Setting up the publishing process..."}
                {publishPhase === "generating" &&
                  "Creating personalized PDF reports for each student..."}
                {publishPhase === "downloading" &&
                  "Preparing download files..."}
                {publishPhase === "sending" &&
                  "Sending email notifications to students..."}
                {publishPhase === "completed" &&
                  "All operations completed successfully!"}
              </div>

              {publishPhase !== "completed" && (
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#9ca3af",
                    textAlign: "center",
                    fontStyle: "italic",
                  }}
                >
                  Please don't close this window while processing...
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {isManualEntryTest && (
        <Modal
          isOpen={isMarksModalOpen}
          onClose={() => !isSaving && setIsMarksModalOpen(false)}
          maxWidth="100%"
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "1.25rem",
              flexWrap: "wrap",
              gap: "1rem",
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: "1.4rem" }}>Enter Marks for Students</h2>
              <p style={{ margin: "0.25rem 0 0 0", color: "#64748b", fontSize: "0.9rem" }}>
                Enter marks for each student manually or auto-fill directly from an Excel spreadsheet.
              </p>
            </div>
            <div className="excel-upload-wrapper">
              <button
                type="button"
                className="excel-upload-btn"
                onClick={() => setIsAutofillModalOpen(true)}
                disabled={isSaving || allStudentsMarks.length === 0}
                style={{ padding: "0.6rem 1.1rem", fontSize: "0.9rem" }}
              >
                <FontAwesomeIcon icon={faFileExcel} />
                <span>Autofill from Excel</span>
              </button>
              <button
                type="button"
                className="template-download-link"
                onClick={handleDirectDownloadTemplate}
                title="Download marks template Excel file"
                disabled={isSaving || allStudentsMarks.length === 0}
              >
                <FontAwesomeIcon icon={faDownload} />
                <span>Don't have a file? Download template</span>
              </button>
            </div>
          </div>
          <form onSubmit={handleSaveAllMarks}>
            <div style={{ marginBottom: "1.5rem", overflowX: "auto" }}>
              <ModalTableInput
                headers={getManualEntryHeaders()}
                rows={getManualEntryRows()}
                onRowChange={handleRowChange}
                renderCell={renderManualEntryCell}
              />
            </div>
            {isSaving ? (
              <Loader />
            ) : (
              <button type="submit" className="btn-submit">
                Save All Marks
              </button>
            )}
          </form>
        </Modal>
      )}

      {/* Autofill Marks from Excel Modal */}
      {isManualEntryTest && (
        <AutofillMarksModal
          isOpen={isAutofillModalOpen}
          onClose={() => setIsAutofillModalOpen(false)}
          students={allStudentsMarks}
          tasks={tasks}
          testName={testInfo?.name || "Manual_Entry_Exam"}
          onAutofill={handleApplyAutofill}
        />
      )}

      {/* Feedback Modal */}
      {isManualEntryTest && (
        <Modal
          isOpen={feedbackModal.isOpen}
          onClose={() =>
            setFeedbackModal({
              isOpen: false,
              studentIndex: null,
              taskIndex: null,
              feedback: "",
            })
          }
        >
          <h2>Add Feedback</h2>
          <p style={{ marginBottom: "1rem", color: "#666" }}>
            {feedbackModal.studentIndex !== null &&
              feedbackModal.taskIndex !== null && (
                <>
                  Student:{" "}
                  <strong>
                    {allStudentsMarks[feedbackModal.studentIndex]?.studentName}
                  </strong>
                  <br />
                  Task:{" "}
                  <strong>
                    {tasks[feedbackModal.taskIndex]?.questionText}
                  </strong>
                </>
              )}
          </p>
          <div className="form-group">
            <label>Feedback:</label>
            <input
              value={feedbackModal.feedback}
              onChange={(e) =>
                setFeedbackModal((prev) => ({
                  ...prev,
                  feedback: e.target.value,
                }))
              }
              placeholder="Enter feedback for this question..."
              rows={5}
              style={{
                width: "100%",
                padding: "0.75rem",
                borderRadius: "6px",
                border: "1px solid #ddd",
                fontSize: "0.95rem",
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSaveFeedback();
                }
              }}
            />
          </div>
          <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
            <button
              type="submit"
              className="btn-submit"
              onClick={handleSaveFeedback}
            >
              Save Feedback
            </button>
          </div>
        </Modal>
      )}

      {/* Export Selection Modal */}
      {isExportModalOpen && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={(e) => {
            if (!isExportInProgress && e.target === e.currentTarget) {
              setIsExportModalOpen(false);
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "2rem",
              maxWidth: "450px",
              width: "90%",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              position: "relative",
            }}
          >
            {/* Close X button */}
            {!isExportInProgress && (
              <button
                onClick={() => setIsExportModalOpen(false)}
                style={{
                  position: "absolute",
                  top: "1rem",
                  right: "1rem",
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  color: "#9ca3af",
                  cursor: "pointer",
                  padding: "0.25rem",
                  lineHeight: 1,
                  borderRadius: "4px",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#374151";
                  e.currentTarget.style.backgroundColor = "#f3f4f6";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#9ca3af";
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                ✕
              </button>
            )}
            {!isExportInProgress ? (
              <>
                <h2
                  style={{
                    margin: "0 0 0.5rem 0",
                    fontSize: "1.5rem",
                    color: "#1a1a1a",
                  }}
                >
                  Export Scores
                </h2>
                <p
                  style={{
                    color: "#666",
                    marginBottom: "1.5rem",
                    fontSize: "0.95rem",
                  }}
                >
                  Choose the type of export you need:
                </p>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                  }}
                >
                  <button
                    onClick={() => {
                      setExportType("summarized");
                      downloadSummarizedExport();
                    }}
                    style={{
                      padding: "1rem 1.5rem",
                      borderRadius: "12px",
                      border: "2px solid #e5e7eb",
                      backgroundColor: "#f9fafb",
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#f09a21";
                      e.currentTarget.style.backgroundColor = "#eff6ff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#e5e7eb";
                      e.currentTarget.style.backgroundColor = "#f9fafb";
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: "1rem",
                        color: "#1a1a1a",
                        marginBottom: "0.25rem",
                      }}
                    >
                      📋 Summarized Scores (Quick)
                    </div>
                    <div style={{ fontSize: "0.85rem", color: "#666" }}>
                      CSV with student names, status, and total scores. Fast
                      export.
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      setExportType("detailed");
                      downloadDetailedExport();
                    }}
                    style={{
                      padding: "1rem 1.5rem",
                      borderRadius: "12px",
                      border: "2px solid #e5e7eb",
                      backgroundColor: "#f9fafb",
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#10b981";
                      e.currentTarget.style.backgroundColor = "#ecfdf5";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#e5e7eb";
                      e.currentTarget.style.backgroundColor = "#f9fafb";
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: "1rem",
                        color: "#1a1a1a",
                        marginBottom: "0.25rem",
                      }}
                    >
                      📊 Detailed Scores (Complete)
                    </div>
                    <div style={{ fontSize: "0.85rem", color: "#666" }}>
                      Excel with question-level breakdown. May take longer time.
                    </div>
                  </button>
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "1rem 0" }}>
                <div style={{ marginBottom: "1.5rem" }}>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      repeat: Infinity,
                      duration: 1,
                      ease: "linear",
                    }}
                    style={{
                      width: "48px",
                      height: "48px",
                      border: "4px solid #e5e7eb",
                      borderTopColor:
                        exportType === "summarized" ? "#f09a21" : "#10b981",
                      borderRadius: "50%",
                      margin: "0 auto",
                    }}
                  />
                </div>
                <h3
                  style={{
                    margin: "0 0 0.5rem 0",
                    fontSize: "1.25rem",
                    color: "#1a1a1a",
                  }}
                >
                  {exportType === "summarized"
                    ? "Preparing Summarized Export"
                    : "Preparing Detailed Export"}
                </h3>
                <motion.p
                  key={exportProgress.message}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    color: "#666",
                    marginBottom: "1.5rem",
                    fontSize: "0.95rem",
                    minHeight: "1.5rem",
                  }}
                >
                  {exportProgress.message}
                </motion.p>
                <div
                  style={{
                    width: "100%",
                    height: "6px",
                    backgroundColor: "#e5e7eb",
                    borderRadius: "3px",
                    overflow: "hidden",
                  }}
                >
                  <motion.div
                    initial={{ width: "0%" }}
                    animate={{
                      width:
                        exportType === "summarized"
                          ? `${(exportProgress.step / 5) * 100}%`
                          : `${(exportProgress.step / 8) * 100}%`,
                    }}
                    transition={{ duration: 0.3 }}
                    style={{
                      height: "100%",
                      backgroundColor:
                        exportType === "summarized" ? "#f09a21" : "#10b981",
                      borderRadius: "3px",
                    }}
                  />
                </div>
                <p
                  style={{
                    color: "#9ca3af",
                    fontSize: "0.8rem",
                    marginTop: "0.75rem",
                  }}
                >
                  {exportType === "detailed" &&
                    "This may take a moment for large classes..."}
                </p>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Notify All Modal */}
      {isNotifyAllModalOpen && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsNotifyAllModalOpen(false);
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "2rem",
              maxWidth: "450px",
              width: "90%",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              position: "relative",
            }}
          >
            {/* Close X button */}
            <button
              onClick={() => setIsNotifyAllModalOpen(false)}
              style={{
                position: "absolute",
                top: "1rem",
                right: "1rem",
                background: "none",
                border: "none",
                fontSize: "1.5rem",
                color: "#9ca3af",
                cursor: "pointer",
                padding: "0.25rem",
                lineHeight: 1,
                borderRadius: "4px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#374151";
                e.currentTarget.style.backgroundColor = "#f3f4f6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#9ca3af";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              ✕
            </button>
            <h2
              style={{
                margin: "0 0 0.5rem 0",
                fontSize: "1.5rem",
                color: "#1a1a1a",
              }}
            >
              Notify All Students
            </h2>
            <p
              style={{
                color: "#666",
                marginBottom: "1.5rem",
                fontSize: "0.95rem",
              }}
            >
              Choose how you want to notify all students:
            </p>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              {/* WhatsApp Option */}
              <button
                onClick={() => {
                  setIsNotifyAllModalOpen(false);
                  handleNotifyAllStudents("whatsapp");
                }}
                style={{
                  padding: "1rem 1.5rem",
                  borderRadius: "12px",
                  border: "2px solid #e5e7eb",
                  backgroundColor: "#f9fafb",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#25D366";
                  e.currentTarget.style.backgroundColor = "#dcfce7";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "1rem",
                    color: "#1a1a1a",
                    marginBottom: "0.25rem",
                  }}
                >
                  <i
                    className="fa-brands fa-whatsapp"
                    style={{ color: "#25D366", marginRight: "0.5rem" }}
                  ></i>
                  WhatsApp
                </div>
                <div style={{ fontSize: "0.85rem", color: "#666" }}>
                  Send score reports via WhatsApp to all students.
                </div>
              </button>

              {/* Email Option */}
              <button
                onClick={() => {
                  setIsNotifyAllModalOpen(false);
                  handleNotifyAllStudents("email");
                }}
                style={{
                  padding: "1rem 1.5rem",
                  borderRadius: "12px",
                  border: "2px solid #e5e7eb",
                  backgroundColor: "#f9fafb",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#3b82f6";
                  e.currentTarget.style.backgroundColor = "#dbeafe";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "1rem",
                    color: "#1a1a1a",
                    marginBottom: "0.25rem",
                  }}
                >
                  <i
                    className="fa-solid fa-envelope"
                    style={{ color: "#3b82f6", marginRight: "0.5rem" }}
                  ></i>
                  Email
                </div>
                <div style={{ fontSize: "0.85rem", color: "#666" }}>
                  Send score reports via email to all students.
                </div>
              </button>

              {/* Push Option */}
              <button
                onClick={() => {
                  setIsNotifyAllModalOpen(false);
                  handleNotifyAllStudents("push");
                }}
                style={{
                  padding: "1rem 1.5rem",
                  borderRadius: "12px",
                  border: "2px solid #e5e7eb",
                  backgroundColor: "#f9fafb",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#f59e0b";
                  e.currentTarget.style.backgroundColor = "#fef3c7";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "1rem",
                    color: "#1a1a1a",
                    marginBottom: "0.25rem",
                  }}
                >
                  <i
                    className="fa-solid fa-bell"
                    style={{ color: "#f59e0b", marginRight: "0.5rem" }}
                  ></i>
                  Push Notification
                </div>
                <div style={{ fontSize: "0.85rem", color: "#666" }}>
                  Send score reports via push notification to all students.
                </div>
              </button>

              {/* All Channels Option */}
              <button
                onClick={() => {
                  setIsNotifyAllModalOpen(false);
                  handleNotifyAllStudents("all");
                }}
                style={{
                  padding: "1rem 1.5rem",
                  borderRadius: "12px",
                  border: "2px solid #e5e7eb",
                  backgroundColor: "#f9fafb",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#8b5cf6";
                  e.currentTarget.style.backgroundColor = "#f3e8ff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "1rem",
                    color: "#1a1a1a",
                    marginBottom: "0.25rem",
                  }}
                >
                  <i
                    className="fa-solid fa-paper-plane"
                    style={{ color: "#8b5cf6", marginRight: "0.5rem" }}
                  ></i>
                  All Channels
                </div>
                <div style={{ fontSize: "0.85rem", color: "#666" }}>
                  Send score reports via Email, WhatsApp, and Push notification to all students.
                </div>
              </button>

              {/* Download Option */}
              <button
                onClick={() => {
                  setIsNotifyAllModalOpen(false);
                  handleDownloadAllWithPrompt();
                }}
                style={{
                  padding: "1rem 1.5rem",
                  borderRadius: "12px",
                  border: "2px solid #e5e7eb",
                  backgroundColor: "#f9fafb",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#f09a21";
                  e.currentTarget.style.backgroundColor = "#fef3c7";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "1rem",
                    color: "#1a1a1a",
                    marginBottom: "0.25rem",
                  }}
                >
                  <i
                    className="fa-solid fa-download"
                    style={{ color: "#f09a21", marginRight: "0.5rem" }}
                  ></i>
                  Download All Reports
                </div>
                <div style={{ fontSize: "0.85rem", color: "#666" }}>
                  Download all score reports as a PDF files.
                </div>
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Regrade Progress Modal */}
      {isRegradeModalOpen && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "2rem",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              textAlign: "center",
            }}
          >
            <div style={{ marginBottom: "1.5rem" }}>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: "linear",
                }}
                style={{
                  width: "48px",
                  height: "48px",
                  border: "4px solid #e5e7eb",
                  borderTopColor: "#f59e0b",
                  borderRadius: "50%",
                  margin: "0 auto",
                }}
              />
            </div>
            <h3
              style={{
                margin: "0 0 0.5rem 0",
                fontSize: "1.25rem",
                color: "#1a1a1a",
              }}
            >
              Marking for Re-grade
            </h3>
            <motion.p
              key={regradeProgress.message}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                color: "#666",
                marginBottom: "1rem",
                fontSize: "0.95rem",
                minHeight: "1.5rem",
              }}
            >
              {regradeProgress.message}
            </motion.p>
            <p
              style={{
                color: "#9ca3af",
                fontSize: "0.85rem",
                marginBottom: "1rem",
              }}
            >
              {regradeProgress.current} of {regradeProgress.total} papers
              processed
            </p>
            <div
              style={{
                width: "100%",
                height: "8px",
                backgroundColor: "#e5e7eb",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <motion.div
                initial={{ width: "0%" }}
                animate={{
                  width:
                    regradeProgress.total > 0
                      ? `${
                          (regradeProgress.current / regradeProgress.total) *
                          100
                        }%`
                      : "0%",
                }}
                transition={{ duration: 0.3 }}
                style={{
                  height: "100%",
                  backgroundColor: "#f59e0b",
                  borderRadius: "4px",
                }}
              />
            </div>
            <p
              style={{
                color: "#9ca3af",
                fontSize: "0.75rem",
                marginTop: "1rem",
              }}
            >
              Please don't close this window...
            </p>
          </motion.div>
        </div>
      )}
      {/* Bulk Blank Annotation PDF Reorder & Download Modal */}
      <BulkAnnotationModal
        isOpen={isBulkAnnotationModalOpen}
        onClose={() => setIsBulkAnnotationModalOpen(false)}
        testId={testId}
        testName={testDetails?.name || testInfo?.name || "Assessment"}
        annotationPrintingOffsetConfigId={
          testDetails?.annotationPrintingOffsetConfigId || testInfo?.annotationPrintingOffsetConfigId
        }
        serverUrl={serverUrl}
        fetchWithAuth={fetchWithAuth}
        swalSuccess={swalSuccess}
        swalError={swalError}
        swalWarning={swalWarning}
      />
      {/* Smart Split Summary & PDF-like Document Viewer Modal */}
      <SmartSplitDocumentViewer
        isOpen={isSmartSplitModalOpen}
        onClose={() => {
          setIsSmartSplitModalOpen(false);
          setSmartSplitResult(null);
          fetchSmartSplitStatus();
        }}
        smartSplitResult={smartSplitResult}
        testId={testId}
        onPageRotated={(pageNumber, newAngle) => {
          setSmartSplitResult((prev) => {
            if (!prev) return prev;
            const nextPages = (prev.pages || []).map((p) =>
              p.pageNumber === pageNumber ? { ...p, rotation: newAngle } : p
            );
            return { ...prev, pages: nextPages };
          });
          setSmartSplitStatus((prev) => {
            if (!prev?.data?.pages) return prev;
            const nextPages = prev.data.pages.map((p) =>
              p.pageNumber === pageNumber ? { ...p, rotation: newAngle } : p
            );
            return {
              ...prev,
              data: { ...prev.data, pages: nextPages },
            };
          });
        }}
        onSave={(updatedPapers) => {
          setSmartSplitResult((prev) =>
            prev ? { ...prev, papers: updatedPapers } : prev
          );
        }}
        onSubmissionsUpdated={() => {
          fetchData(page, searchTerm, sortBy, sortOrder);
          fetchSmartSplitStatus();
        }}
      />
      </motion.div>
  );
};

export default SubmittedAssessment;

